// src/pages/StudentTutorialPlayer.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useLocation } from 'react-router-dom';
import { studentTutorialAPI, studentSlideshowAPI, tutorialAttemptAPI, studentTutorialLinkAPI } from '../services/api';
import EmbeddedSlideshow from '../components/EmbeddedSlideshow';
import ReactMarkdown from 'react-markdown';
import { io } from 'socket.io-client';
import { socketAuth } from '../utils/socketAuth';
import CircularTimer from '../components/CircularTimer';
import axios from 'axios';
import classlabsLogo from './classlabs.png';

const SOCKET_SERVER_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const buildGlossaryPanels = (glossary = [], keyTermsPerMatchingPage = null) => {
    const items = Array.isArray(glossary) ? glossary : [];
    if (items.length === 0) return [];

    // SEN one-at-a-time mode: each term gets its own panel with a decoy definition
    if (Number(keyTermsPerMatchingPage) === 1) {
        return items.map((item, index) => {
            const decoySource = items.find((_, i) => i !== index) || null;
            const decoyDefinitions = decoySource?.definition ? [decoySource.definition] : [];
            return { terms: [item], decoyDefinitions };
        });
    }

    // Explicit override (2 or 4): chunk into fixed-size panels
    const explicitCount = [2, 4].includes(Number(keyTermsPerMatchingPage)) ? Number(keyTermsPerMatchingPage) : null;
    if (explicitCount) {
        const panels = [];
        for (let i = 0; i < items.length; i += explicitCount) {
            const terms = items.slice(i, i + explicitCount);
            if (terms.length > 0) panels.push({ terms, decoyDefinitions: [] });
        }
        return panels;
    }

    // Default auto-split based on total term count:
    // ≤4 terms → 1 panel; 5–8 terms → 2 panels (ceil/floor split)
    if (items.length <= 4) {
        return [{ terms: items, decoyDefinitions: [] }];
    }
    const firstSize = Math.ceil(items.length / 2);
    return [
        { terms: items.slice(0, firstSize), decoyDefinitions: [] },
        { terms: items.slice(firstSize), decoyDefinitions: [] }
    ];
};

const buildStepSequence = (panelCount = 0, hasSequencing = false, hasCategorisation = false) => {
    const base = ['SLIDESHOW', 'EXPLAINER'];
    if (hasSequencing) base.push('SEQUENCE_1');
    if (hasCategorisation) base.push('CATEGORISE_1');
    if (panelCount <= 0) return [...base, 'QUIZ_1', 'QUIZ_2'];

    const steps = [...base];
    for (let i = 1; i <= panelCount; i += 1) {
        steps.push(`GLOSSARY_${i}`);
        if (i === 1) steps.push('QUIZ_1');
        if (i === 2) steps.push('QUIZ_2');
    }

    if (!steps.includes('QUIZ_1')) steps.push('QUIZ_1');
    if (!steps.includes('QUIZ_2')) steps.push('QUIZ_2');
    return steps;
};


const resolvePreferredFont = (studentTutorial = null) => {
    const allowed = ['OpenDyslexic', 'Nunito', 'Arial'];
    const studentProfile = studentTutorial?.studentId && typeof studentTutorial.studentId === 'object'
        ? studentTutorial.studentId
        : null;
    const font = studentProfile?.tutorialFont;
    return allowed.includes(font) ? font : 'OpenDyslexic';
};

const resolvePreferredKeyTermsPerPage = (studentTutorial = null) => {
    const allowed = [1, 2, 4];
    if (!studentTutorial) return null; // null = use auto-split

    const tutorialCustom = studentTutorial?.customTutorialStructure === true;
    const tutorialKeyTerms = Number(studentTutorial?.keyTermsPerMatchingPage);

    const studentProfile = studentTutorial?.studentId && typeof studentTutorial.studentId === 'object'
        ? studentTutorial.studentId
        : null;
    const studentCustom = studentProfile?.customTutorialStructure === true;
    const studentKeyTerms = Number(studentProfile?.keyTermsPerMatchingPage);

    if (tutorialCustom && allowed.includes(tutorialKeyTerms)) return tutorialKeyTerms;
    if (studentCustom && allowed.includes(studentKeyTerms)) return studentKeyTerms;

    return null; // null = use auto-split
};

const buildAdaptiveStepPlan = (panelCount = 0, keyTermsPerMatchingPage = 4, hasSequencing = false, hasCategorisation = false) => {
    const normalizedKeyTerms = [1, 2, 4].includes(Number(keyTermsPerMatchingPage))
        ? Number(keyTermsPerMatchingPage)
        : 4;

    const steps = ['SLIDESHOW', 'EXPLAINER'];
    if (hasSequencing) steps.push('SEQUENCE_1');
    if (hasCategorisation) steps.push('CATEGORISE_1');
    const quizLimits = {};

    if (panelCount <= 0) {
        steps.push('QUIZ_1', 'QUIZ_2');
        quizLimits.QUIZ_1 = 5;
        quizLimits.QUIZ_2 = 5;
        return { steps, quizLimits };
    }

    if (normalizedKeyTerms === 2) {
        let quizBlock = 1;
        for (let panelIndex = 1; panelIndex <= panelCount; panelIndex += 2) {
            steps.push(`GLOSSARY_${panelIndex}`);
            if (panelIndex + 1 <= panelCount) {
                steps.push(`GLOSSARY_${panelIndex + 1}`);
            }
            const quizStep = `QUIZ_${quizBlock}`;
            steps.push(quizStep);
            quizLimits[quizStep] = 5;
            quizBlock += 1;
        }
        return { steps, quizLimits };
    }

    if (normalizedKeyTerms === 1) {
        let quizBlock = 1;
        let useThreeQuestionBlock = true;
        for (let panelIndex = 1; panelIndex <= panelCount; panelIndex += 2) {
            steps.push(`GLOSSARY_${panelIndex}`);
            if (panelIndex + 1 <= panelCount) {
                steps.push(`GLOSSARY_${panelIndex + 1}`);
            }

            const quizStep = `QUIZ_${quizBlock}`;
            steps.push(quizStep);
            quizLimits[quizStep] = useThreeQuestionBlock ? 3 : 2;
            useThreeQuestionBlock = !useThreeQuestionBlock;
            quizBlock += 1;
        }
        return { steps, quizLimits };
    }

    // Default (4 terms/page): original cadence.
    const fallback = buildStepSequence(panelCount, hasSequencing, hasCategorisation);
    const fallbackQuizSteps = fallback.filter((stepName) => stepName.startsWith('QUIZ_'));
    fallbackQuizSteps.forEach((quizStep) => {
        quizLimits[quizStep] = 5;
    });
    return { steps: fallback, quizLimits };
};

const parseEnabledPreviewSteps = (value = '') => {
    const steps = String(value || '')
        .split(',')
        .map(step => step.trim())
        .filter(Boolean);
    return steps.length > 0 ? new Set(steps) : null;
};

const filterStepPlanByEnabledSteps = (plan, enabledSteps = null) => {
    if (!enabledSteps || !plan?.steps?.length) return plan;

    const steps = plan.steps.filter(step => enabledSteps.has(step));
    if (steps.length === 0) return plan;

    const quizLimits = {};
    Object.entries(plan.quizLimits || {}).forEach(([step, limit]) => {
        if (steps.includes(step)) quizLimits[step] = limit;
    });

    return { ...plan, steps, quizLimits };
};

const tutorialHasSequencing = (generatedContent = []) => (
    (Array.isArray(generatedContent) ? generatedContent : []).some((chunk) => (
        Array.isArray(chunk?.sequencing?.steps) && chunk.sequencing.steps.length > 0
    ))
);

const tutorialHasCategorisation = (generatedContent = []) => (
    (Array.isArray(generatedContent) ? generatedContent : []).some((chunk) => (
        (Array.isArray(chunk?.categorisation?.itemsA) && chunk.categorisation.itemsA.length > 0)
        || (Array.isArray(chunk?.categorisation?.itemsB) && chunk.categorisation.itemsB.length > 0)
    ))
);

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    paleBlue: '#E8F4F8',
    softPeach: '#FFF0F0',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F',
    success: '#81C784'
};

const LearningSafetyModal = ({ isOpen, onClose }) => {
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const sectionTitleStyle = {
        fontFamily: 'Nunito, sans-serif',
        fontWeight: 800,
        color: BRAND.charcoal,
        marginBottom: '6px',
        fontSize: '1.05rem'
    };

    const sectionBodyStyle = {
        margin: 0,
        color: '#5D7375',
        lineHeight: 1.65,
        fontSize: '0.98rem',
        fontWeight: 500
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(45, 64, 66, 0.65)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1100,
                padding: '20px'
            }}
        >
            <div
                className="dash-card"
                onClick={(event) => event.stopPropagation()}
                style={{
                    background: 'white',
                    width: '100%',
                    maxWidth: '680px',
                    borderRadius: '16px',
                    border: `2px solid ${BRAND.charcoal}`,
                    boxShadow: '6px 6px 0px rgba(45, 64, 66, 0.18)',
                    padding: '26px 24px 22px 24px',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: BRAND.charcoal, fontSize: '1.45rem' }}>
                        🛡️ How We Keep Your Learning Safe
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-base"
                        style={{ background: '#F0F2F5', color: BRAND.charcoal, padding: '6px 12px', fontSize: '0.85rem' }}
                        aria-label="Close learning safety information"
                    >
                        Close ✕
                    </button>
                </div>

                <div style={{ display: 'grid', gap: '16px' }}>
                    <div style={{ background: '#F9FCFC', border: `1px solid ${BRAND.teal}`, borderRadius: '12px', padding: '14px 16px' }}>
                        <div style={sectionTitleStyle}>What do we know about you?</div>
                        <p style={sectionBodyStyle}>Your teacher tells us your name and what kind of help you need with your learning so we can make these tutorials perfect for you.</p>
                    </div>

                    <div style={{ background: '#FFF9F8', border: `1px solid ${BRAND.coral}`, borderRadius: '12px', padding: '14px 16px' }}>
                        <div style={sectionTitleStyle}>How do we use your answers?</div>
                        <p style={sectionBodyStyle}>When you answer questions in this tutorial, we show your scores to your teacher so they know how to help you best in your next lesson.</p>
                    </div>

                    <div style={{ background: '#F6F8FA', border: '1px solid #C6D0D2', borderRadius: '12px', padding: '14px 16px' }}>
                        <div style={sectionTitleStyle}>Do we keep your information forever?</div>
                        <p style={sectionBodyStyle}>No! When the school year is over, we delete your tutorial scores and your profile.</p>
                    </div>
                </div>

                <p style={{ marginTop: '18px', marginBottom: 0, color: BRAND.charcoal, fontWeight: 700, lineHeight: 1.6 }}>
                    If you have any questions about this, you can always ask your teacher!
                </p>
            </div>
        </div>
    );
};

// --- HELPER: Render Markdown (Forces Bold for * and **) ---
const RenderMarkdown = ({ children, inline = false }) => (
    <ReactMarkdown 
        components={{ 
            p: inline ? 'span' : 'p',
            em: ({node, ...props}) => <strong style={{fontWeight:'800', color: BRAND.charcoal}} {...props} />,
            strong: ({node, ...props}) => <strong style={{fontWeight:'800', color: BRAND.charcoal}} {...props} />
        }}
    >
        {children}
    </ReactMarkdown>
);

// --- HELPER: Render Markdown for MCQ options WITHOUT bold emphasis ---
const RenderMarkdownNoBold = ({ children, inline = false }) => (
    <ReactMarkdown
        components={{
            p: inline ? 'span' : 'p',
            em: ({ node, ...props }) => <span style={{ fontWeight: '400', color: 'inherit' }} {...props} />,
            strong: ({ node, ...props }) => <span style={{ fontWeight: '400', color: 'inherit' }} {...props} />
        }}
    >
        {children}
    </ReactMarkdown>
);

const stripMarkdownFormatting = (value = '') => String(value)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1');

const escapeRegex = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const ensureTranslationLabel = (label = 'Translation') => {
    const clean = String(label || 'Translation').trim();
    if (!clean) return 'Translation:';
    return clean.endsWith(':') ? clean : `${clean}:`;
};

const HoverableTranslationText = ({ text = '', translationPairs = [], translationLabel = 'Translation' }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const sanitizedText = stripMarkdownFormatting(text);

    if (!text) return null;
    if (!Array.isArray(translationPairs) || translationPairs.length === 0) {
        return <>{sanitizedText}</>;
    }

    const filteredPairs = translationPairs
        .filter(pair => pair?.term && pair?.translation)
        .sort((a, b) => b.term.length - a.term.length);

    if (filteredPairs.length === 0) return <>{sanitizedText}</>;

    const combinedRegex = new RegExp(`(${filteredPairs.map(pair => escapeRegex(pair.term)).join('|')})`, 'gi');
    const parts = String(sanitizedText).split(combinedRegex);

    return (
        <>
            {parts.map((part, index) => {
                const pair = filteredPairs.find(p => p.term.toLowerCase() === part.toLowerCase());
                if (!pair) return <span key={`text-${index}`}>{part}</span>;

                return (
                    <span
                        key={`term-${index}`}
                        style={{ position: 'relative', display: 'inline-flex',
                            alignItems: 'center',
                            margin: '0 auto', fontWeight: '700', color: BRAND.charcoal, cursor: 'help' }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                    >
                        {part}
                        {hoveredIndex === index && (
                            <span style={{
                                position: 'absolute',
                                left: '50%',
                                top: 'calc(100% + 6px)',
                                transform: 'translateX(-50%)',
                                background: '#fff',
                                border: `2px solid ${BRAND.teal}`,
                                borderRadius: '8px',
                                padding: '8px 10px',
                                boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
                                whiteSpace: 'nowrap',
                                zIndex: 12,
                                fontSize: '0.85em',
                                fontWeight: '700',
                                color: BRAND.charcoal
                            }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                                    {ensureTranslationLabel(translationLabel)}
                                </div>
                                {stripMarkdownFormatting(pair.translation)}
                            </span>
                        )}
                    </span>
                );
            })}
        </>
    );
};

const findTranslationForText = (text = '', translationPairs = []) => {
    const normalizedText = String(text || '').trim().toLowerCase();
    if (!normalizedText) return '';

    const pair = (translationPairs || []).find((item) => String(item?.term || '').trim().toLowerCase() === normalizedText);
    return pair?.translation ? stripMarkdownFormatting(pair.translation) : '';
};

const HoverableTranslationInline = ({ children, translation = '', translationLabel = 'Translation' }) => {
    const [rect, setRect] = useState(null);
    const spanRef = useRef(null);
    const cleanTranslation = stripMarkdownFormatting(translation);

    if (!cleanTranslation) return <>{children}</>;

    const handleMouseEnter = () => {
        if (spanRef.current) setRect(spanRef.current.getBoundingClientRect());
    };
    const handleMouseLeave = () => setRect(null);

    const tooltip = rect ? ReactDOM.createPortal(
        <span style={{
            position: 'fixed',
            left: rect.left + rect.width / 2,
            top: rect.bottom + 6,
            transform: 'translateX(-50%)',
            background: '#fff',
            border: `2px solid ${BRAND.teal}`,
            borderRadius: '8px',
            padding: '8px 10px',
            boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            fontSize: '0.85em',
            fontWeight: '700',
            color: BRAND.charcoal,
            pointerEvents: 'none',
        }}>
            <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                {ensureTranslationLabel(translationLabel)}
            </div>
            {cleanTranslation}
        </span>,
        document.body
    ) : null;

    return (
        <span ref={spanRef} style={{ display: 'inline', cursor: 'help' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
            {tooltip}
        </span>
    );
};


// --- HELPER: Build fill-in-the-gaps data from explainer + glossary ---
const buildFillInGapsData = (explainerText = '', glossary = []) => {
    if (!explainerText || !Array.isArray(glossary) || glossary.length === 0) return null;

    // Strip markdown bold markers to get plain text for searching
    const plainText = explainerText.replace(/\*\*/g, '');

    // Find which glossary terms actually appear in the explainer (case-insensitive whole-word-ish match)
    const found = [];
    const seen = new Set();
    glossary.forEach(({ term }) => {
        if (!term || seen.has(term.toLowerCase())) return;
        // Escape special regex chars in term
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(?<![\\w])${escaped}(?![\\w])`, 'i');
        if (regex.test(plainText)) {
            found.push(term);
            seen.add(term.toLowerCase());
        }
    });

    if (found.length === 0) return null;

    // Build the gapped text: replace first occurrence of each found term with a blank token
    let gappedText = plainText;
    const blanks = []; // ordered list of correct answers
    found.forEach((term) => {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(?<![\\w])${escaped}(?![\\w])`, 'i');
        const match = gappedText.match(regex);
        if (match) {
            blanks.push({ id: blanks.length, answer: match[0] }); // preserve original casing from text
            gappedText = gappedText.replace(regex, `___BLANK_${blanks.length - 1}___`);
        }
    });

    return { gappedText, blanks };
};

// --- INTERNAL COMPONENT: FILL-IN-THE-GAPS GAME ---
const FillInGapsGame = ({ explainer, glossary, onComplete, onResult, onInteraction, bgTheme = BRAND.cream, hoverTranslationsEnabled = false, glossaryTranslationPairs = [], translationHintLanguage = 'English' }) => {
    const gapsData = useMemo(() => buildFillInGapsData(explainer, glossary), [explainer, glossary]);

    const [fills, setFills] = useState({}); // { blankId: term }
    const [dragOverBlank, setDragOverBlank] = useState(null); // blankId being hovered during drag
    const [dragOverBank, setDragOverBank] = useState(false);
    const [checked, setChecked] = useState(false);
    const [startTime] = useState(Date.now());

    // Word bank: all answers shuffled — must be called before any early return
    const wordBank = useMemo(() => {
        const words = (gapsData?.blanks || []).map(b => b.answer);
        for (let i = words.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [words[i], words[j]] = [words[j], words[i]];
        }
        return words;
    }, [gapsData]); // eslint-disable-line react-hooks/exhaustive-deps

    // If no gaps could be found, render plain explainer with a Continue button
    if (!gapsData) {
        return (
            <div style={{ background: bgTheme, border: `2px solid ${BRAND.charcoal}`, borderRadius: '16px', padding: '22px 20px', boxShadow: '4px 4px 0px rgba(45,64,66,0.10)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: `2px dashed #E0E0E0`, paddingBottom: '10px' }}>
                    <h3 style={{ margin: 0, fontFamily: 'Nunito', fontWeight: '800', color: BRAND.charcoal }}>Narrative Explainer</h3>
                </div>
                <div style={{ fontSize: '1em', lineHeight: '1.7', color: BRAND.charcoal }}>{explainer}</div>
                <div style={{ textAlign: 'right', marginTop: '20px' }}>
                    <button onClick={() => { if (onInteraction) onInteraction('fill_gaps_skip'); onComplete(); }} className="btn-base" style={{ background: BRAND.coral, color: 'white' }}>Continue →</button>
                </div>
            </div>
        );
    }

    const { gappedText, blanks } = gapsData;
    const usedWords = new Set(Object.values(fills));
    const availableWords = wordBank.filter(w => !usedWords.has(w));
    const allFilled = blanks.every(b => fills[b.id] !== undefined);

    const getWordTranslation = (word) => hoverTranslationsEnabled
        ? (glossaryTranslationPairs.find(p => p.term.toLowerCase() === word.toLowerCase())?.translation || '')
        : '';

    // --- Drag handlers ---
    const onDragStartBank = (e, word) => {
        e.dataTransfer.setData('word', word);
        e.dataTransfer.setData('source', 'bank');
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragStartBlank = (e, blankId, word) => {
        e.dataTransfer.setData('word', word);
        e.dataTransfer.setData('source', String(blankId));
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDropBlank = (e, blankId) => {
        e.preventDefault();
        setDragOverBlank(null);
        const word = e.dataTransfer.getData('word');
        const source = e.dataTransfer.getData('source');
        if (!word) return;
        setFills(prev => {
            const next = { ...prev };
            // If dragging from another blank, clear that source blank
            if (source !== 'bank') {
                const srcId = parseInt(source, 10);
                // If dropping onto the same blank, no-op
                if (srcId === blankId) return prev;
                // If target blank already has a word, swap
                const displaced = next[blankId];
                delete next[srcId];
                if (displaced) next[srcId] = displaced;
            }
            next[blankId] = word;
            return next;
        });
        if (onInteraction) onInteraction('fill_gaps_place', { blankId, word });
    };

    const onDropBank = (e) => {
        e.preventDefault();
        setDragOverBank(false);
        const source = e.dataTransfer.getData('source');
        if (source === 'bank') return;
        const srcId = parseInt(source, 10);
        setFills(prev => { const next = { ...prev }; delete next[srcId]; return next; });
        if (onInteraction) onInteraction('fill_gaps_clear', { blankId: srcId });
    };

    const handleCheck = () => {
        const duration = Math.round((Date.now() - startTime) / 1000);
        const correctCount = blanks.filter(b => fills[b.id]?.toLowerCase() === b.answer.toLowerCase()).length;
        if (onInteraction) onInteraction('fill_gaps_check', { correctCount, total: blanks.length });
        if (onResult) onResult({ correctCount, total: blanks.length, duration });
        setChecked(true);
    };

    const score = checked ? blanks.filter(b => fills[b.id]?.toLowerCase() === b.answer.toLowerCase()).length : 0;
    const allCorrect = checked && score === blanks.length;
    const segments = gappedText.split(/(___BLANK_\d+___)/);

    return (
        <div style={{ background: bgTheme, border: `2px solid ${BRAND.charcoal}`, borderRadius: '16px', padding: '22px 20px', boxShadow: '4px 4px 0px rgba(45,64,66,0.10)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: `2px dashed #E0E0E0`, paddingBottom: '10px' }}>
                <h3 style={{ margin: 0, fontFamily: 'Nunito', fontWeight: '800', color: BRAND.charcoal }}>
                    ✏️ Fill in the Gaps
                </h3>
            </div>

            {!checked && (
                <p style={{ margin: '0 0 16px', color: '#5D7375', fontSize: '0.92em', lineHeight: 1.55 }}>
                    Drag words from the bank into the gaps. Drag a placed word back to the bank to remove it.
                </p>
            )}

            {/* Word bank — also a drop target to return words */}
            {!checked && (
                <div
                    onDragOver={e => { e.preventDefault(); setDragOverBank(true); }}
                    onDragLeave={() => setDragOverBank(false)}
                    onDrop={onDropBank}
                    style={{
                        display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px',
                        background: dragOverBank ? '#EFF9FA' : '#F8F9FA',
                        borderRadius: '10px',
                        border: `2px dashed ${dragOverBank ? BRAND.teal : '#D0D5D6'}`,
                        marginBottom: '20px', minHeight: '44px',
                        transition: 'all 0.1s'
                    }}
                >
                    {availableWords.map((word, i) => {
                        const translation = getWordTranslation(word);
                        return (
                            <span
                                key={i}
                                draggable
                                onDragStart={e => onDragStartBank(e, word)}
                                className="btn-base"
                                style={{
                                    padding: '6px 14px', borderRadius: '20px', fontSize: '0.9em', fontWeight: '700',
                                    background: BRAND.white, color: BRAND.charcoal,
                                    border: `2px solid ${BRAND.charcoal}`,
                                    cursor: 'grab', userSelect: 'none', display: 'inline-block'
                                }}
                            >
                                {translation ? (
                                    <HoverableTranslationInline translation={translation} translationLabel={`${translationHintLanguage} translation`}>
                                        {word}
                                    </HoverableTranslationInline>
                                ) : word}
                            </span>
                        );
                    })}
                    {availableWords.length === 0 && <span style={{ color: '#9AACAE', fontSize: '0.88em', alignSelf: 'center' }}>All words placed</span>}
                </div>
            )}

            {/* Gapped text */}
            <div style={{ fontSize: '1.1em', lineHeight: '2.8', color: BRAND.charcoal, fontFamily: 'Inter', marginBottom: '20px' }}>
                {segments.map((seg, i) => {
                    const blankMatch = seg.match(/^___BLANK_(\d+)___$/);
                    if (!blankMatch) return <span key={i}>{seg}</span>;

                    const blankId = parseInt(blankMatch[1], 10);
                    const blank = blanks[blankId];
                    const fill = fills[blankId];
                    const isCorrect = checked && fill?.toLowerCase() === blank.answer.toLowerCase();
                    const isWrong = checked && fill && fill.toLowerCase() !== blank.answer.toLowerCase();
                    const isEmpty = !fill;
                    const isOver = dragOverBlank === blankId;

                    const borderColor = isCorrect ? BRAND.success : isWrong ? BRAND.danger : isOver ? BRAND.teal : isEmpty ? '#B0C4C6' : BRAND.charcoal;
                    const bgColor = isCorrect ? '#F0FAF0' : isWrong ? '#FEF5F3' : isOver ? '#EFF9FA' : isEmpty ? '#F8F9FA' : '#FFFDE7';

                    return (
                        <span
                            key={i}
                            draggable={!checked && !!fill}
                            onDragStart={!checked && fill ? e => onDragStartBlank(e, blankId, fill) : undefined}
                            onDragOver={!checked ? e => { e.preventDefault(); setDragOverBlank(blankId); } : undefined}
                            onDragLeave={!checked ? () => setDragOverBlank(null) : undefined}
                            onDrop={!checked ? e => onDropBlank(e, blankId) : undefined}
                            style={{
                                display: 'inline-block',
                                minWidth: `${Math.max(70, blank.answer.length * 11)}px`,
                                padding: '1px 10px',
                                lineHeight: '2.1',
                                margin: '0 3px',
                                borderRadius: '6px',
                                border: `2px solid ${borderColor}`,
                                background: bgColor,
                                color: isEmpty ? '#9AACAE' : BRAND.charcoal,
                                fontWeight: isEmpty ? '400' : '700',
                                fontSize: '0.95em',
                                cursor: checked ? 'default' : fill ? 'grab' : 'copy',
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                transition: 'all 0.1s',
                                userSelect: 'none',
                                boxShadow: isOver ? `0 0 0 3px ${BRAND.teal}40` : 'none',
                            }}
                        >
                            {fill ? (() => {
                                const translation = getWordTranslation(fill);
                                return translation ? (
                                    <HoverableTranslationInline translation={translation} translationLabel={`${translationHintLanguage} translation`}>
                                        {fill}
                                    </HoverableTranslationInline>
                                ) : fill;
                            })() : '___'}
                            {isCorrect && <span style={{ marginLeft: '4px', color: BRAND.success }}>✓</span>}
                            {isWrong && <span style={{ marginLeft: '4px', color: BRAND.danger }}>✗</span>}
                        </span>
                    );
                })}
            </div>

            {/* Show correct answers for wrong blanks after check */}
            {checked && blanks.some(b => fills[b.id]?.toLowerCase() !== b.answer.toLowerCase()) && (
                <div style={{ background: '#FFF8E1', border: `2px solid ${BRAND.warning}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.88em' }}>
                    {blanks.filter(b => fills[b.id]?.toLowerCase() !== b.answer.toLowerCase()).map(b => (
                        <div key={b.id} style={{ color: BRAND.charcoal }}>
                            <strong style={{ color: BRAND.danger }}>{fills[b.id] || '(blank)'}</strong> → correct answer: <strong style={{ color: BRAND.success }}>{b.answer}</strong>
                        </div>
                    ))}
                </div>
            )}

            {!checked && (
                <div style={{ textAlign: 'right' }}>
                    <button onClick={handleCheck} disabled={!allFilled} className="btn-base" style={{ background: allFilled ? BRAND.teal : '#B0C4C6', color: 'white', cursor: allFilled ? 'pointer' : 'not-allowed' }}>
                        Check My Answers ✓
                    </button>
                </div>
            )}

            {checked && (
                <div>
                    <div style={{
                        display: 'inline-block',
                        background: allCorrect ? '#E8F5E9' : score > 0 ? '#FFF8E1' : '#FEEFED',
                        border: `2px solid ${allCorrect ? BRAND.success : score > 0 ? BRAND.warning : BRAND.danger}`,
                        borderRadius: '10px', padding: '10px 16px', marginBottom: '16px',
                        fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95em'
                    }}>
                        {allCorrect
                            ? `🎉 Perfect! All ${blanks.length} gaps filled correctly.`
                            : `${score} of ${blanks.length} gaps correct.`}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <button
                            onClick={() => { if (onInteraction) onInteraction('fill_gaps_continue'); onComplete(); }}
                            className="btn-base"
                            style={{ background: BRAND.coral, color: 'white' }}
                        >
                            Continue →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- INTERNAL COMPONENT: SEQUENCING GAME ---
const SequencingGame = ({ sequencing, onComplete, onResult, onInteraction, bgTheme = BRAND.cream, translationSteps = [], translationHintLanguage = 'English' }) => {
    const { title, steps } = sequencing;

    // order: array of original step indices in current display order (starts shuffled)
    const [order, setOrder] = useState(() => {
        const indices = steps.map((_, i) => i);
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        return indices;
    });

    const [dragState, setDragState] = useState(null); // { fromIdx, startY, currentY }
    const [checked, setChecked] = useState(false);
    const [startTime] = useState(Date.now());
    const itemHeightRef = useRef(56); // updated on first drag

    const draggingIdx = dragState?.fromIdx ?? null;
    const draggingDeltaY = dragState ? dragState.currentY - dragState.startY : 0;

    const getTargetIdx = (currentY, startY, fromIdx, itemH) => {
        const shift = Math.round((currentY - startY) / itemH);
        return Math.max(0, Math.min(order.length - 1, fromIdx + shift));
    };

    const targetIdx = dragState
        ? getTargetIdx(dragState.currentY, dragState.startY, dragState.fromIdx, itemHeightRef.current)
        : null;

    const getTranslateY = (listIdx) => {
        if (dragState === null || listIdx === draggingIdx) return 0;
        const from = draggingIdx;
        const to = targetIdx;
        const itemH = itemHeightRef.current;
        if (from < to && listIdx > from && listIdx <= to) return -itemH;
        if (from > to && listIdx < from && listIdx >= to) return itemH;
        return 0;
    };

    const handlePointerDown = (e, listIdx) => {
        if (checked) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        itemHeightRef.current = e.currentTarget.getBoundingClientRect().height + 8;
        setDragState({ fromIdx: listIdx, startY: e.clientY, currentY: e.clientY });
        if (onInteraction) onInteraction('sequence_drag_start', { listIdx });
    };

    const handlePointerMove = (e, listIdx) => {
        if (!dragState || dragState.fromIdx !== listIdx) return;
        e.preventDefault();
        setDragState(prev => ({ ...prev, currentY: e.clientY }));
    };

    const handlePointerUp = (e, listIdx) => {
        if (!dragState || dragState.fromIdx !== listIdx) return;
        const to = getTargetIdx(dragState.currentY, dragState.startY, dragState.fromIdx, itemHeightRef.current);
        if (to !== dragState.fromIdx) {
            setOrder(prev => {
                const next = [...prev];
                const [moved] = next.splice(dragState.fromIdx, 1);
                next.splice(to, 0, moved);
                return next;
            });
            if (onInteraction) onInteraction('sequence_drag_end', { from: dragState.fromIdx, to });
        }
        setDragState(null);
    };

    const handleCheck = () => {
        const duration = Math.round((Date.now() - startTime) / 1000);
        const correctCount = order.filter((origIdx, pos) => origIdx === pos).length;
        if (onInteraction) onInteraction('sequence_check', { correctCount, total: steps.length });
        if (onResult) onResult({ correctCount, total: steps.length, duration });
        setChecked(true);
    };

    const correctness = checked ? order.map((origIdx, pos) => origIdx === pos) : [];
    const score = checked ? correctness.filter(Boolean).length : 0;
    const allCorrect = checked && score === steps.length;

    return (
        <div style={{ background: bgTheme, border: `2px solid ${BRAND.charcoal}`, borderRadius: '16px', padding: '22px 20px', boxShadow: '4px 4px 0px rgba(45,64,66,0.10)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: `2px dashed #E0E0E0`, paddingBottom: '10px' }}>
                <h3 style={{ margin: 0, fontFamily: 'Nunito', fontWeight: '800', color: BRAND.charcoal }}>
                    🔢 Sequencing Activity
                </h3>
            </div>

            {title && (
                <p style={{ margin: '0 0 14px', fontWeight: '700', color: BRAND.charcoal, fontSize: '1.02em', lineHeight: 1.5 }}>
                    {title}
                </p>
            )}

            {!checked && (
                <p style={{ margin: '0 0 18px', color: '#5D7375', fontSize: '0.92em', lineHeight: 1.55 }}>
                    Drag the steps into the correct order, then tap Check.
                </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                {order.map((origIdx, listIdx) => {
                    const isDragging = draggingIdx === listIdx;
                    const translateY = isDragging ? draggingDeltaY : getTranslateY(listIdx);
                    const isCorrect = checked && correctness[listIdx];
                    const isWrong = checked && !correctness[listIdx];

                    return (
                        <div
                            key={origIdx}
                            onPointerDown={!checked ? (e) => handlePointerDown(e, listIdx) : undefined}
                            onPointerMove={!checked ? (e) => handlePointerMove(e, listIdx) : undefined}
                            onPointerUp={!checked ? (e) => handlePointerUp(e, listIdx) : undefined}
                            onPointerCancel={!checked ? (e) => handlePointerUp(e, listIdx) : undefined}
                            style={{
                                display: 'flex', alignItems: 'flex-start', gap: '10px',
                                padding: '10px 12px', borderRadius: '10px',
                                border: `2px solid ${isCorrect ? BRAND.success : isWrong ? BRAND.danger : isDragging ? BRAND.teal : BRAND.charcoal}`,
                                background: isCorrect ? '#F0FAF0' : isWrong ? '#FEF5F3' : isDragging ? '#EFF9FA' : BRAND.white,
                                transform: `translateY(${translateY}px)`,
                                transition: isDragging ? 'box-shadow 0.1s' : 'transform 0.15s ease, border-color 0.15s, background 0.15s',
                                boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.18)' : '2px 2px 0px rgba(45,64,66,0.10)',
                                zIndex: isDragging ? 10 : 2,
                                position: 'relative',
                                cursor: checked ? 'default' : isDragging ? 'grabbing' : 'grab',
                                userSelect: 'none',
                                touchAction: 'none',
                            }}
                        >
                            {!checked && (
                                <span style={{ color: '#9AACAE', fontSize: '1.1em', flexShrink: 0, marginTop: '3px', lineHeight: 1 }}>
                                    ⠿
                                </span>
                            )}
                            <span style={{
                                minWidth: '26px', height: '26px', borderRadius: '50%',
                                background: isCorrect ? BRAND.success : isWrong ? BRAND.danger : BRAND.teal,
                                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: '800', fontSize: '0.78rem', flexShrink: 0
                            }}>
                                {listIdx + 1}
                            </span>
                            <div style={{ flex: 1 }}>
                                <span style={{ fontWeight: '600', color: BRAND.charcoal, fontSize: '0.93em', lineHeight: 1.5 }}>
                                    <HoverableTranslationInline translation={translationSteps[origIdx] || ''} translationLabel={`${translationHintLanguage} translation`}>
                                        {steps[origIdx]}
                                    </HoverableTranslationInline>
                                </span>
                                {isWrong && (
                                    <div style={{ marginTop: '4px', fontSize: '0.8em', color: BRAND.danger, fontWeight: '700' }}>
                                        Correct: <HoverableTranslationInline translation={translationSteps[listIdx] || ''} translationLabel={`${translationHintLanguage} translation`}>{steps[listIdx]}</HoverableTranslationInline>
                                    </div>
                                )}
                            </div>
                            {isCorrect && <span style={{ alignSelf: 'center', color: BRAND.success, fontWeight: '800', flexShrink: 0 }}>✓</span>}
                            {isWrong && <span style={{ alignSelf: 'center', color: BRAND.danger, fontWeight: '800', flexShrink: 0 }}>✗</span>}
                        </div>
                    );
                })}
            </div>

            {!checked && (
                <div style={{ textAlign: 'right' }}>
                    <button onClick={handleCheck} className="btn-base" style={{ background: BRAND.teal, color: 'white' }}>
                        Check My Answer ✓
                    </button>
                </div>
            )}

            {checked && (
                <div>
                    <div style={{
                        display: 'inline-block',
                        background: allCorrect ? '#E8F5E9' : score > 0 ? '#FFF8E1' : '#FEEFED',
                        border: `2px solid ${allCorrect ? BRAND.success : score > 0 ? BRAND.warning : BRAND.danger}`,
                        borderRadius: '10px', padding: '10px 16px', marginBottom: '16px',
                        fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95em'
                    }}>
                        {allCorrect
                            ? `🎉 Perfect! All ${steps.length} steps in the right order.`
                            : `${score} of ${steps.length} steps in the correct position.`}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <button
                            onClick={() => { if (onInteraction) onInteraction('sequence_continue'); onComplete(); }}
                            className="btn-base"
                            style={{ background: BRAND.coral, color: 'white' }}
                        >
                            Continue →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- INTERNAL COMPONENT: CATEGORISATION GAME ---
const CategorisationGame = ({ categorisation, onComplete, onResult, onInteraction, bgTheme = BRAND.cream, translationCategoryA = '', translationCategoryB = '', translationItemsA = [], translationItemsB = [], translationHintLanguage = 'English' }) => {
    const { categoryA, itemsA, categoryB, itemsB } = categorisation;

    // Shuffle all items together, preserving which category each belongs to
    const allItems = useMemo(() => {
        const combined = [
            ...(itemsA || []).map((item, i) => ({ item, correct: 'A', translation: translationItemsA[i] || '' })),
            ...(itemsB || []).map((item, i) => ({ item, correct: 'B', translation: translationItemsB[i] || '' }))
        ];
        for (let i = combined.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [combined[i], combined[j]] = [combined[j], combined[i]];
        }
        return combined;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [assignments, setAssignments] = useState({}); // { itemIndex: 'A' | 'B' }
    const [checked, setChecked] = useState(false);
    const [startTime] = useState(Date.now());

    const unassigned = allItems.filter((_, i) => assignments[i] === undefined);
    const assignedA = allItems.map((obj, i) => ({ ...obj, origIdx: i })).filter(({ origIdx }) => assignments[origIdx] === 'A');
    const assignedB = allItems.map((obj, i) => ({ ...obj, origIdx: i })).filter(({ origIdx }) => assignments[origIdx] === 'B');

    const assignItem = (itemIdx, category) => {
        if (checked) return;
        setAssignments(prev => ({ ...prev, [itemIdx]: category }));
        if (onInteraction) onInteraction('categorise_assign', { itemIdx, category });
    };

    const unassignItem = (itemIdx) => {
        if (checked) return;
        setAssignments(prev => { const next = { ...prev }; delete next[itemIdx]; return next; });
    };

    const allAssigned = unassigned.length === 0;

    const handleCheck = () => {
        const duration = Math.round((Date.now() - startTime) / 1000);
        const correctCount = allItems.filter((obj, i) => assignments[i] === obj.correct).length;
        if (onInteraction) onInteraction('categorise_check', { correctCount, total: allItems.length });
        if (onResult) onResult({ correctCount, total: allItems.length, duration });
        setChecked(true);
    };

    const score = checked ? allItems.filter((obj, i) => assignments[i] === obj.correct).length : 0;
    const allCorrect = checked && score === allItems.length;

    const itemStyle = (origIdx, inCategory) => {
        const isCorrect = checked && assignments[origIdx] === allItems[origIdx].correct;
        const isWrong = checked && assignments[origIdx] !== allItems[origIdx].correct;
        return {
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '6px 10px', borderRadius: '8px', margin: '4px',
            border: `2px solid ${isCorrect ? BRAND.success : isWrong ? BRAND.danger : BRAND.charcoal}`,
            background: isCorrect ? '#F0FAF0' : isWrong ? '#FEF5F3' : BRAND.white,
            fontWeight: '600', fontSize: '0.88em', color: BRAND.charcoal,
            cursor: checked ? 'default' : 'pointer',
            userSelect: 'none'
        };
    };

    return (
        <div style={{ background: bgTheme, border: `2px solid ${BRAND.charcoal}`, borderRadius: '16px', padding: '22px 20px', boxShadow: '4px 4px 0px rgba(45,64,66,0.10)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: `2px dashed #E0E0E0`, paddingBottom: '10px' }}>
                <h3 style={{ margin: 0, fontFamily: 'Nunito', fontWeight: '800', color: BRAND.charcoal }}>
                    🗂️ Categorisation Activity
                </h3>
            </div>

            {!checked && unassigned.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ margin: '0 0 10px', color: '#5D7375', fontSize: '0.92em', lineHeight: 1.55 }}>
                        Tap each item, then choose which category it belongs to.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '12px', background: '#F8F9FA', borderRadius: '10px', border: '2px dashed #D0D5D6', minHeight: '48px' }}>
                        {unassigned.map((obj) => {
                            const origIdx = allItems.indexOf(obj);
                            return (
                                <span key={origIdx} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span
                                        onClick={() => assignItem(origIdx, 'A')}
                                        style={{ ...itemStyle(origIdx, false), borderColor: BRAND.teal, background: '#EFF9FA', cursor: 'pointer' }}
                                        title={`Add to ${categoryA}`}
                                    >
                                        ← A
                                    </span>
                                    <span style={{ fontWeight: '600', fontSize: '0.9em', color: BRAND.charcoal, flex: 1 }}>
                                        <HoverableTranslationInline translation={obj.translation} translationLabel={`${translationHintLanguage} translation`}>{obj.item}</HoverableTranslationInline>
                                    </span>
                                    <span
                                        onClick={() => assignItem(origIdx, 'B')}
                                        style={{ ...itemStyle(origIdx, false), borderColor: BRAND.coral, background: '#FEF5F3', cursor: 'pointer' }}
                                        title={`Add to ${categoryB}`}
                                    >
                                        B →
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[{ label: categoryA, translationLabel: translationCategoryA, side: 'A', items: assignedA, color: BRAND.teal }, { label: categoryB, translationLabel: translationCategoryB, side: 'B', items: assignedB, color: BRAND.coral }].map(({ label, translationLabel, side, items: sideItems, color }) => (
                    <div key={side} style={{ border: `2px solid ${color}`, borderRadius: '12px', padding: '12px', background: BRAND.white, minHeight: '80px' }}>
                        <div style={{ fontWeight: '800', fontSize: '0.82em', letterSpacing: '0.6px', textTransform: 'uppercase', color, marginBottom: '8px' }}>
                            <HoverableTranslationInline translation={translationLabel} translationLabel={`${translationHintLanguage} translation`}>{label}</HoverableTranslationInline>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {sideItems.map(({ item, translation, correct, origIdx }) => {
                                const isCorrect = checked && correct === side;
                                const isWrong = checked && correct !== side;
                                return (
                                    <span
                                        key={origIdx}
                                        onClick={() => unassignItem(origIdx)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            padding: '5px 9px', borderRadius: '7px',
                                            border: `2px solid ${isCorrect ? BRAND.success : isWrong ? BRAND.danger : color}`,
                                            background: isCorrect ? '#F0FAF0' : isWrong ? '#FEF5F3' : '#F8F9FA',
                                            fontWeight: '600', fontSize: '0.85em', color: BRAND.charcoal,
                                            cursor: checked ? 'default' : 'pointer', userSelect: 'none'
                                        }}
                                        title={checked ? undefined : 'Tap to remove'}
                                    >
                                        <HoverableTranslationInline translation={translation} translationLabel={`${translationHintLanguage} translation`}>{item}</HoverableTranslationInline>
                                        {isCorrect && <span style={{ color: BRAND.success, fontWeight: '800' }}>✓</span>}
                                        {isWrong && <span style={{ color: BRAND.danger, fontWeight: '800' }}>✗</span>}
                                        {!checked && <span style={{ color: '#9AACAE', fontSize: '0.85em' }}>✕</span>}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {!checked && (
                <div style={{ textAlign: 'right' }}>
                    <button onClick={handleCheck} disabled={!allAssigned} className="btn-base" style={{ background: allAssigned ? BRAND.teal : '#B0C4C6', color: 'white', cursor: allAssigned ? 'pointer' : 'not-allowed' }}>
                        Check My Answer ✓
                    </button>
                </div>
            )}

            {checked && (
                <div>
                    <div style={{
                        display: 'inline-block',
                        background: allCorrect ? '#E8F5E9' : score > 0 ? '#FFF8E1' : '#FEEFED',
                        border: `2px solid ${allCorrect ? BRAND.success : score > 0 ? BRAND.warning : BRAND.danger}`,
                        borderRadius: '10px', padding: '10px 16px', marginBottom: '16px',
                        fontWeight: '700', color: BRAND.charcoal, fontSize: '0.95em'
                    }}>
                        {allCorrect
                            ? `🎉 Perfect! All ${allItems.length} items correctly sorted.`
                            : `${score} of ${allItems.length} items in the correct category.`}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <button
                            onClick={() => { if (onInteraction) onInteraction('categorise_continue'); onComplete(); }}
                            className="btn-base"
                            style={{ background: BRAND.coral, color: 'white' }}
                        >
                            Continue →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- INTERNAL COMPONENT: DYNAMIC QUIZ (Pop Style) ---
const DynamicQuiz = ({ pool, ignoreIds, limit, onAnswerSubmit, onComplete, onInteraction, onCheckExplainer, narrationEnabled, narrationAudioBaseUrl, defaultAudioSpeed = 1, enableHoverTranslations = false, translationPairs = [], questionTranslationPairs = [], optionTranslationPairs = [], translationHintLanguage = 'English', sourceChunkIndex = 0, bgTheme = BRAND.cream, jcqMode = false }) => {
    const [, setCurrentIndex] = useState(0);
    const [localHistory, setLocalHistory] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [startTime, setStartTime] = useState(Date.now());
    const [questionPlaying, setQuestionPlaying] = useState(false);
    const [questionSpeed, setQuestionSpeed] = useState([0.5, 0.75, 1].includes(Number(defaultAudioSpeed)) ? Number(defaultAudioSpeed) : 1);
    const [questionStack, setQuestionStack] = useState([]);
    const [questionPointer, setQuestionPointer] = useState(-1);
    const [answerStateByQuestion, setAnswerStateByQuestion] = useState({});
    const questionAudioRef = useRef(null);

    useEffect(() => {
        const normalized = [0.5, 0.75, 1].includes(Number(defaultAudioSpeed)) ? Number(defaultAudioSpeed) : 1;
        setQuestionSpeed(normalized);
    }, [defaultAudioSpeed]);

    const objectiveCycle = useMemo(() => {
        const ordered = [];
        const seen = new Set();

        (Array.isArray(pool) ? pool : []).forEach((question) => {
            const objective = (question?.objectiveFocus || '').toString().trim();
            if (!objective) return;

            const key = objective.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            ordered.push(objective);
        });

        return ordered;
    }, [pool]);

    const selectNextQuestion = useCallback(() => {
        const answeredCount = localHistory.length;
        const correctCount = localHistory.filter(h => h.isCorrect).length;
        const rate = answeredCount === 0 ? 1.0 : (correctCount / answeredCount);

        let targetDifficulty = 'medium';
        if (answeredCount > 0) {
            if (rate >= 0.8) targetDifficulty = 'hard';
            else if (rate >= 0.5) targetDifficulty = 'medium';
            else targetDifficulty = 'easy';
        }

        const available = pool.filter(q => !ignoreIds.has(q.question)); 
        if (available.length === 0) return null;

        const targetObjective = objectiveCycle.length > 0
            ? objectiveCycle[answeredCount % objectiveCycle.length]
            : '';

        const byTargetObjective = targetObjective
            ? available.filter(q => ((q?.objectiveFocus || '').toString().trim().toLowerCase() === targetObjective.toLowerCase()))
            : available;

        const preferredPool = byTargetObjective.length > 0 ? byTargetObjective : available;

        let candidates = preferredPool.filter(q => (q.difficulty || '').toLowerCase() === targetDifficulty);
        
        if (candidates.length === 0) {
            if (targetDifficulty === 'easy') candidates = preferredPool.filter(q => (q.difficulty || '').toLowerCase() === 'medium');
            else if (targetDifficulty === 'hard') candidates = preferredPool.filter(q => (q.difficulty || '').toLowerCase() === 'medium');
            else candidates = preferredPool.filter(q => (q.difficulty || '').toLowerCase() === 'easy');
        }

        if (candidates.length === 0) candidates = preferredPool;
        if (candidates.length === 0) candidates = available;
        return candidates[Math.floor(Math.random() * candidates.length)];

    }, [pool, ignoreIds, localHistory, objectiveCycle]);

    useEffect(() => {
        if (!currentQuestion && !isSubmitted) {
            if (localHistory.length >= limit) {
                onComplete();
                return;
            }
            const next = selectNextQuestion();
            if (next) {
                setQuestionStack((prev) => [...prev, next]);
                setQuestionPointer((prev) => prev + 1);
                setCurrentQuestion(next);
                setStartTime(Date.now());
            } else {
                onComplete();
            }
        }
    }, [currentQuestion, isSubmitted, localHistory, limit, onComplete, selectNextQuestion]);

    const handleOptionClick = (key) => {
        if (isSubmitted) return;
        if (onInteraction) onInteraction('quiz_option_select', { question: currentQuestion.question, option: key });
        setSelectedOption(key);
    };

    const handleSubmit = () => {
        if (!selectedOption || isSubmitted) return;
        const duration = Math.round((Date.now() - startTime) / 1000);
        if (onInteraction) onInteraction('quiz_submit', { question: currentQuestion.question, selected: selectedOption, duration });

        const isCorrect = currentQuestion.options[selectedOption] === currentQuestion.options[currentQuestion.correctAnswer];
        const questionKey = `${sourceChunkIndex}:${Number.isFinite(Number(currentQuestion?.sourceQuestionIndex)) ? Number(currentQuestion.sourceQuestionIndex) : localHistory.length}:${String(currentQuestion?.question || '').trim()}`;
        const previousState = answerStateByQuestion[questionKey];
        const selectedAt = jcqMode ? new Date() : null;
        const isExtraTimePhase = jcqMode && (localHistory.length + 1) > Math.ceil(limit * 0.75);
        const changedFromIncorrectToCorrectInExtraTime = Boolean(
            jcqMode && isExtraTimePhase && isCorrect && previousState && previousState.isCorrect === false
        );

        setIsSubmitted(true);
        setFeedback({
            isCorrect,
            correctAnswer: currentQuestion.options[currentQuestion.correctAnswer],
            furtherInformation: currentQuestion.furtherInformation
        });

        const questionIdx = localHistory.length;
        onAnswerSubmit({
            questionIndex: questionIdx,
            sourceQuestionIndex: Number.isFinite(Number(currentQuestion?.sourceQuestionIndex))
                ? Number(currentQuestion.sourceQuestionIndex)
                : questionIdx,
            sourceChunkIndex,
            objectiveFocusId: (currentQuestion?.objectiveFocusId || '').toString().trim(),
            objectiveFocus: (currentQuestion?.objectiveFocusCanonical || currentQuestion?.objectiveFocus || '').toString().trim(),
            questionText: currentQuestion.question,
            studentAnswer: currentQuestion.options[selectedOption],
            isCorrect,
            correctAnswer: currentQuestion.options[currentQuestion.correctAnswer],
            difficulty: currentQuestion.difficulty,
            timeTaken: duration,
            ...(jcqMode ? {
                selectedAt,
                isExtraTimePhase,
                changedFromIncorrectToCorrectInExtraTime
            } : {})
        }, currentQuestion.question);
        if (jcqMode) {
            setAnswerStateByQuestion((prev) => ({
                ...prev,
                [questionKey]: { isCorrect, selectedAt }
            }));
        }

        setLocalHistory(prev => [...prev, { isCorrect, difficulty: currentQuestion.difficulty }]);
    };

    const handleNext = () => {
        if (onInteraction) onInteraction('quiz_next_question', {});
        setSelectedOption(null);
        setIsSubmitted(false);
        setFeedback(null);
        setCurrentQuestion(null);
        setCurrentIndex(prev => prev + 1);
    };

    const handleBack = () => {
        if (questionPointer <= 0) return;
        const prevPointer = questionPointer - 1;
        const previousQuestion = questionStack[prevPointer];
        if (!previousQuestion) return;
        if (onInteraction) onInteraction('quiz_back_question', { from: questionPointer, to: prevPointer });
        setQuestionPointer(prevPointer);
        setCurrentQuestion(previousQuestion);
        setSelectedOption(null);
        setIsSubmitted(false);
        setFeedback(null);
        setStartTime(Date.now());
    };

    const playQuestionAudio = useCallback(() => {
        if (!narrationEnabled || !currentQuestion?.narrationAudioUrl || !questionAudioRef.current) return;

        if (onInteraction) onInteraction('quiz_narration_play', { question: currentQuestion.question });
        questionAudioRef.current.playbackRate = questionSpeed;
        questionAudioRef.current.play().catch((e) => console.error(e));
    }, [currentQuestion, narrationEnabled, onInteraction, questionSpeed]);

    const toggleQuestionAudio = () => {
        if (!questionAudioRef.current || !currentQuestion?.narrationAudioUrl || !narrationEnabled) return;
        if (questionPlaying) {
            questionAudioRef.current.pause();
        } else {
            playQuestionAudio();
        }
    };

    const cycleQuestionSpeed = () => {
        const speeds = [0.5, 0.75, 1];
        const nextIdx = (speeds.indexOf(questionSpeed) + 1) % speeds.length;
        const nextSpeed = speeds[nextIdx];
        setQuestionSpeed(nextSpeed);
        if (questionAudioRef.current) {
            questionAudioRef.current.playbackRate = nextSpeed;
        }
    };

    useEffect(() => {
        if (!narrationEnabled || !questionAudioRef.current) return;
        questionAudioRef.current.pause();
        questionAudioRef.current.currentTime = 0;
        setQuestionPlaying(false);

        if (currentQuestion?.narrationAudioUrl) {
            questionAudioRef.current.src = narrationAudioBaseUrl(currentQuestion.narrationAudioUrl);
            questionAudioRef.current.playbackRate = questionSpeed;
            questionAudioRef.current.load();
        }

        const questionAudioEl = questionAudioRef.current;

        return () => {
            if (questionAudioEl) questionAudioEl.pause();
        };
    }, [currentQuestion, narrationEnabled, narrationAudioBaseUrl, questionSpeed]);

    if (!currentQuestion) return <div style={{padding:'20px', fontStyle:'italic'}}>Loading Question...</div>;

    const getOptionStyle = (key) => {
        const baseStyle = {
            padding: '18px', borderRadius: '12px', border: `2px solid ${BRAND.charcoal}`,
            textAlign: 'left', fontSize: '1.15em', cursor: 'pointer',
            transition: 'all 0.1s', fontFamily: 'Inter, sans-serif',
            marginBottom: '10px', background: bgTheme, color: BRAND.charcoal,
            boxShadow: '4px 4px 0px rgba(45,64,66,0.1)'
        };

        if (!isSubmitted) {
            if (selectedOption === key) {
                return { ...baseStyle, background: '#E0F2F1', borderColor: BRAND.teal, boxShadow: `2px 2px 0px ${BRAND.teal}`, transform: 'translateY(2px)' };
            }
            return baseStyle;
        }

        const isSelected = selectedOption === key;
        const isCorrectOption = key === currentQuestion.correctAnswer;
        
        if (isCorrectOption) return { ...baseStyle, backgroundColor: BRAND.success, color: 'white', borderColor: BRAND.charcoal };
        if (isSelected && !isCorrectOption) return { ...baseStyle, backgroundColor: BRAND.danger, color: 'white', borderColor: BRAND.charcoal };
        
        return { ...baseStyle, opacity: 0.5, cursor: 'default', boxShadow: 'none' };
    };

    const currentQuestionNumber = isSubmitted ? localHistory.length : localHistory.length + 1;

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <span style={{ fontWeight: '800', color: BRAND.charcoal, fontFamily: 'Nunito', fontSize: '1.2em' }}>
                    Question {currentQuestionNumber} / {limit === 999 ? 'End' : limit}
                </span>
                {narrationEnabled && currentQuestion?.narrationAudioUrl && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            onClick={toggleQuestionAudio}
                            className="btn-base"
                            style={{ padding: '6px 12px', fontSize: '0.85em', background: BRAND.teal, color: 'white' }}
                        >
                            {questionPlaying ? 'Pause' : 'Play'} Audio
                        </button>
                        <button
                            onClick={cycleQuestionSpeed}
                            className="btn-base"
                            style={{ padding: '6px 12px', fontSize: '0.85em', background: 'white', color: BRAND.charcoal }}
                        >
                            {questionSpeed}x
                        </button>
                    </div>
                )}
            </div>

            <audio ref={questionAudioRef} style={{ display: 'none' }} onPlay={() => setQuestionPlaying(true)} onPause={() => setQuestionPlaying(false)} onEnded={() => setQuestionPlaying(false)} />

            <div style={{ fontSize: '1.3em', fontWeight: '600', marginBottom: '25px', fontFamily: 'Nunito', color: BRAND.charcoal }}>
                {enableHoverTranslations ? (
                    <HoverableTranslationInline translation={findTranslationForText(currentQuestion.question, questionTranslationPairs)} translationLabel={`${translationHintLanguage} translation`}>
                        <RenderMarkdown inline>{currentQuestion.question}</RenderMarkdown>
                    </HoverableTranslationInline>
                ) : (
                    <RenderMarkdown inline>{currentQuestion.question}</RenderMarkdown>
                )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {Object.entries(currentQuestion.options).map(([key, value]) => (
                    <button 
                        key={key} 
                        onClick={() => handleOptionClick(key)}
                        style={getOptionStyle(key)}
                    >
                        <strong style={{marginRight:'10px', fontFamily:'Nunito'}}>{key}.</strong> 
                        {enableHoverTranslations ? (
                            <HoverableTranslationText text={value} translationPairs={optionTranslationPairs.length ? optionTranslationPairs : translationPairs} translationLabel={`${translationHintLanguage} translation`} />
                        ) : (
                            <RenderMarkdownNoBold inline>{value}</RenderMarkdownNoBold>
                        )}
                    </button>
                ))}
            </div>

            {!isSubmitted && (
                <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                    {jcqMode && (
                        <button
                            onClick={handleBack}
                            disabled={questionPointer <= 0}
                            className="btn-base"
                            style={{ background: 'white', color: BRAND.charcoal, borderColor: BRAND.charcoal, cursor: questionPointer <= 0 ? 'not-allowed' : 'pointer', opacity: questionPointer <= 0 ? 0.6 : 1 }}
                        >
                            ← Back
                        </button>
                    )}
                    <button
                        onClick={onCheckExplainer}
                        className="btn-base"
                        style={{ background: 'white', color: BRAND.charcoal, borderColor: BRAND.charcoal }}
                    >
                        🔍 Check Explainer
                    </button>

                    <button 
                        onClick={handleSubmit} 
                        disabled={!selectedOption}
                        className="btn-base"
                        style={{ 
                            background: selectedOption ? BRAND.teal : '#ddd', 
                            color: selectedOption ? 'white' : '#888',
                            borderColor: BRAND.charcoal,
                            cursor: selectedOption ? 'pointer' : 'not-allowed',
                            fontSize: '1.1em'
                        }}
                    >
                        Submit Answer
                    </button>
                </div>
            )}

            {isSubmitted && (
                <div style={{ marginTop: '30px', animation: 'fadeIn 0.3s' }}>
                    <div style={{ 
                        padding: '20px', borderRadius: '12px', 
                        backgroundColor: feedback.isCorrect ? '#E8F5E9' : '#FFEBEE',
                        border: `2px solid ${BRAND.charcoal}`,
                        boxShadow: '4px 4px 0px rgba(0,0,0,0.1)'
                    }}>
                        <h4 style={{ margin: '0 0 10px 0', fontFamily: 'Nunito', fontSize: '1.2em', color: feedback.isCorrect ? '#2E7D32' : '#C62828' }}>
                            {feedback.isCorrect ? "🎉 That's correct!" : "🤔 Not quite."}
                        </h4>
                        {!feedback.isCorrect && (
                            <p style={{ margin: 0, fontFamily: 'Inter' }}>The correct answer was: <strong><RenderMarkdown inline>{feedback.correctAnswer}</RenderMarkdown></strong></p>
                        )}
                        {feedback.furtherInformation && (
                            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #ccc', fontSize: '0.95em', color: '#555' }}>
                                <strong>Explanation:</strong> <RenderMarkdown inline>{feedback.furtherInformation}</RenderMarkdown>
                            </div>
                        )}
                    </div>
                    <div style={{ marginTop: '20px', textAlign: 'right' }}>
                        <button 
                            onClick={handleNext}
                            className="btn-base"
                            style={{ background: BRAND.charcoal, color: 'white' }}
                        >
                            Next Question →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- INTERNAL COMPONENT: GLOSSARY MATCHING GAME (Drag and Drop) ---
const GlossaryMatchingGame = ({ terms, extraDefinitions = [], onComplete, onMatchResult, onInteraction, onCheckExplainer, narrationEnabled, narrationAudioUrl, narrationAudioBaseUrl, defaultAudioSpeed = 1, showTranslationHints = false, translationHintLanguage = 'English', bgTheme = BRAND.cream }) => {
    const [leftTerms, setLeftTerms] = useState([]); 
    const [rightDefinitions, setRightDefinitions] = useState([]); 
    const [matches, setMatches] = useState({}); 
    const [matchedTermIndices, setMatchedTermIndices] = useState(new Set());
    const [attempts, setAttempts] = useState({}); 
    const [startTime, setStartTime] = useState(Date.now());
    const [shakeId, setShakeId] = useState(null); 
    const [activeDragIndex, setActiveDragIndex] = useState(null); 
    const [activeDropIndex, setActiveDropIndex] = useState(null); 
    const [selectedTermIndex, setSelectedTermIndex] = useState(null);
    const [isSkipping, setIsSkipping] = useState(false);
    const [glossaryPlaying, setGlossaryPlaying] = useState(false);
    const [glossarySpeed, setGlossarySpeed] = useState([0.5, 0.75, 1].includes(Number(defaultAudioSpeed)) ? Number(defaultAudioSpeed) : 1);
    const [hoveredTermIndex, setHoveredTermIndex] = useState(null);
    const glossaryAudioRef = useRef(null);
    const draggedIndexRef = useRef(null);
    const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
    const isPhone = viewportWidth < 768;

    useEffect(() => {
        const normalized = [0.5, 0.75, 1].includes(Number(defaultAudioSpeed)) ? Number(defaultAudioSpeed) : 1;
        setGlossarySpeed(normalized);
    }, [defaultAudioSpeed]);

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const termsIdentifier = terms
        .map((t) => `${t?.term || ''}::${t?.definition || ''}`)
        .join('||') + `::extras::${(extraDefinitions || []).join('||')}`;

    useEffect(() => {
        const indexedTerms = terms.map((t, i) => ({ ...t, originalIndex: i }));
        setLeftTerms(indexedTerms);

        const decoyDefinitionEntries = (extraDefinitions || [])
            .filter(Boolean)
            .map((definition, idx) => ({
                term: '',
                definition,
                originalIndex: -1 * (idx + 1)
            }));

        // Definitions (decoys included) in a random order (15 Oct): the old shift-by-one order with decoys
        // last was a pattern a pupil could learn. Never left lined up with their own terms. Matching uses
        // originalIndex, so order doesn't affect marking.
        const definitions = [...indexedTerms, ...decoyDefinitionEntries];
        const shuffle = () => {
            for (let i = definitions.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [definitions[i], definitions[j]] = [definitions[j], definitions[i]];
            }
        };
        const linedUp = () => indexedTerms.length > 1 && indexedTerms.every((t, i) => definitions[i]?.originalIndex === t.originalIndex);
        shuffle();
        for (let tries = 0; linedUp() && tries < 5; tries++) shuffle();
        if (linedUp()) definitions.push(definitions.shift());

        setRightDefinitions(definitions);

        setMatches({});
        setMatchedTermIndices(new Set());
        setAttempts({});
        setStartTime(Date.now());
        setIsSkipping(false);
        setHoveredTermIndex(null);
        setSelectedTermIndex(null);
    }, [termsIdentifier]); 

    const handleDragStart = (e, termIndex) => {
        e.dataTransfer.setData("text/plain", String(termIndex));
        e.dataTransfer.effectAllowed = "move";
        draggedIndexRef.current = termIndex;
        setActiveDragIndex(termIndex);
        if (onInteraction) onInteraction('glossary_drag_start', { termIndex });
    };

    const handleDragEnd = () => {
        draggedIndexRef.current = null;
        setActiveDragIndex(null);
        setActiveDropIndex(null);
    };

    const handleDragEnter = (e, defIndex) => {
        e.preventDefault();
        setActiveDropIndex(defIndex);
    };

    const handleDragOver = (e) => {
        e.preventDefault(); 
        e.dataTransfer.dropEffect = "move";
    };

    const processMatchAttempt = (draggedTermIndex, targetDefIndex) => {
        if (draggedTermIndex === null || draggedTermIndex === undefined || matchedTermIndices.has(draggedTermIndex)) {
            return;
        }

        if (onInteraction) onInteraction('glossary_drop', { draggedTermIndex, targetDefIndex });

        if (draggedTermIndex === targetDefIndex) {
            const termObj = terms[draggedTermIndex];
            setMatches(prev => ({ ...prev, [targetDefIndex]: termObj }));
            setMatchedTermIndices(prev => new Set(prev).add(draggedTermIndex));

            const duration = Math.round((Date.now() - startTime) / 1000);
            const termAttempts = (attempts[draggedTermIndex] || 0) + 1;
            if (onMatchResult) onMatchResult(termObj, termAttempts, duration);
            setStartTime(Date.now());

            if (Object.keys(matches).length + 1 === terms.length) {
                setTimeout(onComplete, 1000);
            }
        } else {
            setAttempts(prev => ({ ...prev, [draggedTermIndex]: (prev[draggedTermIndex] || 0) + 1 }));
            setShakeId(targetDefIndex);
            setTimeout(() => setShakeId(null), 500);
        }
    };

    const handleDrop = (e, targetDefIndex) => {
        e.preventDefault();
        const rawDraggedIndex = e.dataTransfer.getData("text/plain");
        const parsedDraggedIndex = Number.parseInt(rawDraggedIndex, 10);

        let draggedTermIndex = parsedDraggedIndex;
        if (Number.isNaN(draggedTermIndex)) {
            draggedTermIndex = draggedIndexRef.current;
        }
        if (draggedTermIndex === null || draggedTermIndex === undefined) {
            draggedTermIndex = activeDragIndex;
        }

        draggedIndexRef.current = null;
        setActiveDragIndex(null);
        setActiveDropIndex(null);

        processMatchAttempt(draggedTermIndex, targetDefIndex);
    };

    const handleTermTap = (termIndex) => {
        if (matchedTermIndices.has(termIndex)) return;
        setSelectedTermIndex(termIndex);
        if (onInteraction) onInteraction('glossary_term_select', { termIndex });
    };

    const handleDefinitionTap = (targetDefIndex) => {
        if (selectedTermIndex === null || selectedTermIndex === undefined) return;
        processMatchAttempt(selectedTermIndex, targetDefIndex);
        setSelectedTermIndex(null);
    };

    const handleSkip = async () => {
        if (isSkipping) return;
        setIsSkipping(true);
        if (onInteraction) onInteraction('glossary_skip', {});
        
        for (let i = 0; i < terms.length; i++) {
             if (!matchedTermIndices.has(i)) {
                 if (onMatchResult) {
                     await onMatchResult(terms[i], 3, 0);
                 }
             }
        }
        
        setIsSkipping(false);
        onComplete();
    };

    const playGlossaryAudio = useCallback(() => {
        if (!narrationEnabled || !narrationAudioUrl || !glossaryAudioRef.current) return;
        if (onInteraction) onInteraction('glossary_narration_play', { termsCount: terms.length });
        glossaryAudioRef.current.playbackRate = glossarySpeed;
        glossaryAudioRef.current.play().catch((e) => console.error(e));
    }, [narrationEnabled, narrationAudioUrl, onInteraction, terms.length, glossarySpeed]);

    const toggleGlossaryAudio = () => {
        if (!glossaryAudioRef.current || !narrationAudioUrl || !narrationEnabled) return;
        if (glossaryPlaying) {
            glossaryAudioRef.current.pause();
        } else {
            playGlossaryAudio();
        }
    };

    const cycleGlossarySpeed = () => {
        const speeds = [0.5, 0.75, 1];
        const nextIdx = (speeds.indexOf(glossarySpeed) + 1) % speeds.length;
        const nextSpeed = speeds[nextIdx];
        setGlossarySpeed(nextSpeed);
        if (glossaryAudioRef.current) {
            glossaryAudioRef.current.playbackRate = nextSpeed;
        }
    };

    useEffect(() => {
        if (!narrationEnabled || !glossaryAudioRef.current) return;
        glossaryAudioRef.current.pause();
        glossaryAudioRef.current.currentTime = 0;
        setGlossaryPlaying(false);

        if (narrationAudioUrl) {
            glossaryAudioRef.current.src = narrationAudioBaseUrl(narrationAudioUrl);
            glossaryAudioRef.current.playbackRate = glossarySpeed;
            glossaryAudioRef.current.load();
        }

        const glossaryAudioEl = glossaryAudioRef.current;

        return () => {
            if (glossaryAudioEl) glossaryAudioEl.pause();
        };
    }, [narrationAudioUrl, narrationEnabled, narrationAudioBaseUrl, termsIdentifier, glossarySpeed]);

    return (
        <div style={{ padding: isPhone ? '14px' : '25px', background: 'white', borderRadius: '16px', border: `2px solid ${BRAND.charcoal}`, boxShadow: '4px 4px 0px rgba(45,64,66,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isPhone ? 'flex-start' : 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ margin: 0, color: BRAND.charcoal, fontFamily: 'Nunito', fontWeight: '800', fontSize: isPhone ? '1.05rem' : '1.3rem' }}>Match Terms to Definitions</h3>
                {narrationEnabled && narrationAudioUrl && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                            onClick={toggleGlossaryAudio}
                            className="btn-base"
                            style={{ padding: '6px 12px', fontSize: '0.85em', background: BRAND.teal, color: 'white' }}
                        >
                            {glossaryPlaying ? 'Pause' : 'Play'} Audio
                        </button>
                        <button
                            onClick={cycleGlossarySpeed}
                            className="btn-base"
                            style={{ padding: '6px 12px', fontSize: '0.85em', background: 'white', color: BRAND.charcoal }}
                        >
                            {glossarySpeed}x
                        </button>
                    </div>
                )}
            </div>
            <p style={{ textAlign: 'center', marginBottom: showTranslationHints ? '8px' : '25px', color: '#666', fontStyle: 'italic', fontSize: '0.9em' }}>Drag the terms on the left to their correct definitions on the right, or tap a term then tap its matching definition on touchscreens.</p>
            {showTranslationHints && (
                <p style={{ textAlign: 'center', marginBottom: '25px', color: '#5D7375', fontSize: '0.85em', fontWeight: '600' }}>
                    💡 Hover over a key term to see the {translationHintLanguage} translation.
                </p>
            )}

            <audio ref={glossaryAudioRef} style={{ display: 'none' }} onPlay={() => setGlossaryPlaying(true)} onPause={() => setGlossaryPlaying(false)} onEnded={() => setGlossaryPlaying(false)} />
            
            <div style={{ display: 'flex', gap: isPhone ? '14px' : '30px', flexDirection: isPhone ? 'column' : 'row', flexWrap: 'wrap' }}>
                
                {/* Left Column: Draggable Terms (Fixed Position) */}
                <div style={{ flex: isPhone ? 'unset' : 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {leftTerms.map((item) => {
                        const isMatched = matchedTermIndices.has(item.originalIndex);
                        const isDragging = activeDragIndex === item.originalIndex;
                        const isSelectedForTap = selectedTermIndex === item.originalIndex;
                        
                        return (
                            <div 
                                key={`term-${item.originalIndex}`}
                                draggable={!isMatched} 
                                onDragStart={(e) => !isMatched && handleDragStart(e, item.originalIndex)}
                                onDragEnd={handleDragEnd}
                                onMouseEnter={() => setHoveredTermIndex(item.originalIndex)}
                                onMouseLeave={() => setHoveredTermIndex(null)}
                                onClick={() => !isMatched && handleTermTap(item.originalIndex)}
                                style={{
                                    padding: isPhone ? '12px 14px' : '15px 20px', borderRadius: '12px', 
                                    cursor: isMatched ? 'default' : (isDragging ? 'grabbing' : 'grab'),
                                    background: isMatched ? '#198754' : (isDragging ? BRAND.teal : (isSelectedForTap ? '#EAF4F4' : '#F9FAFB')),
                                    border: `2px solid ${isMatched ? '#198754' : (isSelectedForTap ? BRAND.charcoal : BRAND.teal)}`,
                                    color: isMatched || isDragging ? 'white' : BRAND.charcoal, 
                                    fontFamily: 'Nunito', fontWeight: '700', fontSize: isPhone ? '1em' : '1.1em',
                                    boxShadow: isDragging ? `0 0 10px ${BRAND.teal}` : '2px 2px 0px rgba(0,0,0,0.05)',
                                    textAlign: 'center',
                                    transition: 'all 0.2s',
                                    opacity: isMatched ? 0.9 : (isDragging ? 0.8 : 1),
                                    transform: isDragging ? 'scale(1.05)' : 'none',
                                    position: 'relative'
                                }}
                            >
                                <RenderMarkdown inline>{item.term}</RenderMarkdown>
                                {isMatched && <span style={{marginLeft:'8px'}}>✓</span>}
                                {showTranslationHints && hoveredTermIndex === item.originalIndex && item.translationTerm && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 6px)',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        minWidth: '180px',
                                        maxWidth: '260px',
                                        background: '#fff',
                                        color: BRAND.charcoal,
                                        border: `2px solid ${BRAND.teal}`,
                                        borderRadius: '10px',
                                        padding: '8px 10px',
                                        boxShadow: '4px 4px 0px rgba(45,64,66,0.12)',
                                        zIndex: 5,
                                        fontFamily: 'Inter',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        lineHeight: 1.35
                                    }}>
                                        <RenderMarkdown inline>{item.translationTerm}</RenderMarkdown>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Right Column: Drop Zones (Definitions) */}
                <div style={{ flex: isPhone ? 'unset' : 2, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {rightDefinitions.map((item) => {
                        const isMatched = !!matches[item.originalIndex];
                        const matchedTerm = matches[item.originalIndex];
                        const isShaking = shakeId === item.originalIndex;
                        const isHovered = activeDropIndex === item.originalIndex && !isMatched;

                        const matchedBg = '#d1e7dd'; 
                        const matchedBorder = '#198754';
                        const matchedText = '#0f5132';

                        const hoverBg = '#EAF4F4';
                        const hoverBorder = BRAND.teal;

                        return (
                            <div
                                key={`def-${item.originalIndex}`}
                                onDragEnter={(e) => !isMatched && handleDragEnter(e, item.originalIndex)}
                                onDragOver={!isMatched ? handleDragOver : undefined}
                                onDrop={!isMatched ? (e) => handleDrop(e, item.originalIndex) : undefined}
                                onClick={() => !isMatched && handleDefinitionTap(item.originalIndex)}
                                style={{
                                    padding: isPhone ? '12px' : '15px', borderRadius: '12px', 
                                    background: isMatched ? matchedBg : (isHovered ? hoverBg : bgTheme),
                                    border: `2px solid ${isMatched ? matchedBorder : (isShaking ? BRAND.danger : (isHovered ? hoverBorder : '#E0E0E0'))}`,
                                    color: isMatched ? matchedText : '#555',
                                    transition: 'all 0.2s', fontSize: '0.95em', fontFamily: 'Inter',
                                    boxShadow: '4px 4px 0px rgba(45,64,66,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    transform: isShaking ? 'translateX(5px)' : (isHovered ? 'scale(1.01)' : 'none')
                                }}
                            >
                                <div style={{flex: 1}}>
                                    {showTranslationHints ? (
                                        <HoverableTranslationInline translation={item.translationDefinition} translationLabel={`${translationHintLanguage} translation`}>
                                            <RenderMarkdown inline>{item.definition}</RenderMarkdown>
                                        </HoverableTranslationInline>
                                    ) : (
                                        <RenderMarkdown inline>{item.definition}</RenderMarkdown>
                                    )}
                                </div>
                                
                                {isMatched ? (
                                    <div style={{
                                        marginLeft: isPhone ? '10px' : '15px', padding: '6px 12px', borderRadius: '8px', 
                                        background: matchedBorder, color: 'white', fontWeight: 'bold', fontSize: '0.9em',
                                        minWidth: isPhone ? '68px' : '100px', textAlign:'center'
                                    }}>
                                        <RenderMarkdown inline>{matchedTerm.term}</RenderMarkdown>
                                    </div>
                                ) : (
                                    <div style={{ marginLeft: isPhone ? '10px' : '15px', width: isPhone ? '68px' : '100px', height: '30px' }}></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* BUTTON ROW */}
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                <button 
                    onClick={onCheckExplainer}
                    className="btn-base"
                    style={{ background: 'white', color: BRAND.charcoal, borderColor: BRAND.charcoal }}
                >
                    🔍 Check Explainer
                </button>

                <button 
                    onClick={handleSkip}
                    disabled={isSkipping}
                    className="btn-base"
                    style={{ background: BRAND.charcoal, color: 'white', opacity: isSkipping ? 0.7 : 1, cursor: isSkipping ? 'not-allowed' : 'pointer' }}
                >
                    {isSkipping ? 'Skipping...' : 'Skip Section →'}
                </button>
            </div>
        </div>
    );
};

const countWords = (text = '') => String(text || '').trim().split(/\s+/).filter(Boolean).length;

const ExtendedWritingResponseSection = ({ prompt = {}, onComplete, onInteraction, bgTheme = BRAND.cream }) => {
    const [standardText, setStandardText] = useState('');
    const [extraTimeText, setExtraTimeText] = useState('');
    const [error, setError] = useState('');
    const marks = Number.isFinite(Number(prompt.marks)) ? Number(prompt.marks) : 5;
    const totalWords = countWords(`${standardText} ${extraTimeText}`);

    const handleSubmit = () => {
        if (!standardText.trim() && !extraTimeText.trim()) {
            setError('Write your response before finishing this section.');
            return;
        }

        if (onInteraction) {
            onInteraction('jcq_extended_writing_submit', {
                marks,
                wordCount: totalWords,
                usedExtraTime: Boolean(extraTimeText.trim())
            });
        }

        onComplete({
            text: standardText.trim(),
            extraTimeText: extraTimeText.trim(),
            wordCount: totalWords
        });
    };

    return (
        <div style={{ background: bgTheme, border: `3px solid ${BRAND.charcoal}`, borderRadius: 20, boxShadow: '6px 6px 0 rgba(45,64,66,0.12)', padding: 24 }}>
            <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', marginBottom: 14 }}>
                <span style={{ background: BRAND.coral, border: `2px solid ${BRAND.charcoal}`, borderRadius: 999, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: 900, padding: '7px 12px' }}>
                    Extended Writing · {marks} marks
                </span>
                <span style={{ color: BRAND.muted, fontWeight: 800 }}>{totalWords} words</span>
            </div>

            <h2 style={{ color: BRAND.charcoal, fontFamily: 'Nunito, sans-serif', fontSize: '1.55rem', margin: '0 0 10px' }}>
                Written response
            </h2>
            <p style={{ color: BRAND.charcoal, fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.5, margin: '0 0 18px' }}>
                <RenderMarkdown inline>{prompt.question || 'Answer the extended writing question set by your teacher.'}</RenderMarkdown>
            </p>

            <label style={{ color: BRAND.charcoal, display: 'block', fontWeight: 900, marginBottom: 14 }}>
                Standard-time response
                <textarea
                    value={standardText}
                    onChange={(event) => { setStandardText(event.target.value); setError(''); }}
                    rows={8}
                    placeholder="Write your answer here first."
                    style={{ border: `2px solid ${BRAND.charcoal}`, borderRadius: 14, boxSizing: 'border-box', display: 'block', fontFamily: 'inherit', fontSize: '1rem', lineHeight: 1.5, marginTop: 8, padding: 14, resize: 'vertical', width: '100%' }}
                />
            </label>

            <label style={{ color: BRAND.charcoal, display: 'block', fontWeight: 900 }}>
                Extra-time additions
                <textarea
                    value={extraTimeText}
                    onChange={(event) => { setExtraTimeText(event.target.value); setError(''); }}
                    rows={5}
                    placeholder="If you use extra time, add the words you write during extra time here. These words are highlighted for your teacher."
                    style={{ border: '2px solid #0066CC', borderRadius: 14, boxSizing: 'border-box', color: '#0066CC', display: 'block', fontFamily: 'inherit', fontSize: '1rem', fontWeight: 700, lineHeight: 1.5, marginTop: 8, padding: 14, resize: 'vertical', width: '100%' }}
                />
            </label>

            {error && <div role="alert" style={{ background: '#FFF3F0', border: `2px solid ${BRAND.coral}`, borderRadius: 12, color: BRAND.charcoal, fontWeight: 800, marginTop: 14, padding: 12 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <button type="button" onClick={handleSubmit} className="btn-base" style={{ background: BRAND.teal, color: 'white' }}>
                    Finish Extended Writing →
                </button>
            </div>
        </div>
    );
};

// +++ UPDATED: Accept user prop +++
const StudentTutorialPlayer = ({ user, sharedMode = false }) => {
    const { studentTutorialId, shareToken } = useParams();
    const location = useLocation();
    const isEmbedded = new URLSearchParams(location.search).get('embedded') === '1';

    const [studentTutorial, setStudentTutorial] = useState(null);
    const [allSlideshows, setAllSlideshows] = useState(new Map());
    const [attempt, setAttempt] = useState(null);

    const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isComplete, setIsComplete] = useState(false);

    const [isTrackerOpen, setIsTrackerOpen] = useState(!sharedMode && !isEmbedded);
    const [isReviewingExplainer, setIsReviewingExplainer] = useState(false);

    const [globalUsedQuestionIds, setGlobalUsedQuestionIds] = useState(new Set());

    const [explainerPlaying, setExplainerPlaying] = useState(false);
    const [explainerSpeed, setExplainerSpeed] = useState(1);
    const explainerAudioRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fontScale, setFontScale] = useState(1);
    const [bgTheme, setBgTheme] = useState(BRAND.cream);
    const [isLearningSafetyModalOpen, setIsLearningSafetyModalOpen] = useState(false);
    const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
    const [viewportHeight, setViewportHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 900));
    const [isExtendedWritingActive, setIsExtendedWritingActive] = useState(false);
    const [jcqExtendedResponse, setJcqExtendedResponse] = useState(null);

    const isJcqMode = new URLSearchParams(location.search).get('jcq') === '1' ||
                      Boolean(studentTutorial?.homeworkId?.isJcqTest);
    const jcqExtendedWritingPrompt = ['BUCKET_A', 'BUCKET_WRITING'].includes(studentTutorial?.homeworkId?.jcqBucketId)
        ? studentTutorial?.homeworkId?.jcqExtendedWritingPrompt
        : null;
    const shouldCollectJcqExtendedWriting = Boolean(isJcqMode && jcqExtendedWritingPrompt?.question);
    // Writing-only JCQ tests have no MCQ content; the player goes straight to the writing section.
    const isWritingOnlyTest = studentTutorial?.homeworkId?.jcqBucketId === 'BUCKET_WRITING';
    const preferredFont = resolvePreferredFont(studentTutorial);
    const tutorialFontClass = preferredFont === 'OpenDyslexic' ? 'font-opendyslexic'
        : preferredFont === 'Nunito' ? 'font-nunito'
        : 'font-arial';
    const isSharedAccess = sharedMode && !!shareToken;
    const [hasStartedSharedTutorial, setHasStartedSharedTutorial] = useState(!isSharedAccess);
    const previewModeFromQuery = new URLSearchParams(location.search).get('previewMode') === '1';
    const jumpTo = new URLSearchParams(location.search).get('jumpTo');
    const enabledStepsFromQuery = new URLSearchParams(location.search).get('enabledSteps') || '';
    const enabledPreviewSteps = useMemo(() => parseEnabledPreviewSteps(enabledStepsFromQuery), [enabledStepsFromQuery]);
    const isPreviewAccess = !isSharedAccess && (!!location.state?.previewMode || previewModeFromQuery);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.type === 'JUMP_TO_STEP' && event.data?.step && studentTutorial) {
                const pk = resolvePreferredKeyTermsPerPage(studentTutorial);
                const chunk = studentTutorial.generatedContent?.[currentChunkIndex] || null;
                const panels = buildGlossaryPanels(chunk?.glossary || [], pk);
                const hasSeq = Boolean(chunk?.sequencing?.steps?.length > 0);
                const hasCat = Boolean(chunk?.categorisation?.itemsA?.length > 0);
                const plan = filterStepPlanByEnabledSteps(buildAdaptiveStepPlan(panels.length, pk, hasSeq, hasCat), enabledPreviewSteps);

                const targetIdx = plan.steps.indexOf(event.data.step);
                if (targetIdx !== -1) {
                    setCurrentStepIndex(targetIdx);
                }
            }
        };
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [studentTutorial, currentChunkIndex, enabledPreviewSteps]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (jumpTo && studentTutorial) {
            const preferredKeyTerms = resolvePreferredKeyTermsPerPage(studentTutorial);
            const chunk = studentTutorial.generatedContent?.[currentChunkIndex] || null;
            const panels = buildGlossaryPanels(chunk?.glossary || [], preferredKeyTerms);
            const hasSeq = Boolean(chunk?.sequencing?.steps?.length > 0);
            const hasCat = Boolean(chunk?.categorisation?.itemsA?.length > 0);
            const plan = filterStepPlanByEnabledSteps(buildAdaptiveStepPlan(panels.length, preferredKeyTerms, hasSeq, hasCat), enabledPreviewSteps);

            const targetIdx = plan.steps.indexOf(jumpTo);
            if (targetIdx !== -1 && targetIdx !== currentStepIndex) {
                setCurrentStepIndex(targetIdx);
            }
        }
    }, [jumpTo, studentTutorial, currentChunkIndex, enabledPreviewSteps]); // eslint-disable-line react-hooks/exhaustive-deps

    const isMobileViewport = viewportWidth < 768;
    const shouldDockSafetyButtonBelowTutorial = isMobileViewport || viewportHeight < 760;

    // Derived state for current step
    const { currentStep, currentStepPlan } = useMemo(() => {
        if (!studentTutorial) return { currentStep: 'SLIDESHOW', currentStepPlan: null };
        const pk = resolvePreferredKeyTermsPerPage(studentTutorial);
        const panels = buildGlossaryPanels((studentTutorial?.generatedContent?.[currentChunkIndex]?.glossary || []), pk);
        const hasSeq = tutorialHasSequencing(studentTutorial?.generatedContent || []);
        const hasCat = tutorialHasCategorisation(studentTutorial?.generatedContent || []);
        const plan = filterStepPlanByEnabledSteps(buildAdaptiveStepPlan(panels.length, pk, hasSeq, hasCat), enabledPreviewSteps);
        return { currentStep: plan.steps[currentStepIndex] || plan.steps[0] || 'SLIDESHOW', currentStepPlan: plan };
    }, [studentTutorial, currentChunkIndex, currentStepIndex, enabledPreviewSteps]);

    useEffect(() => {
        if (currentStepPlan?.steps?.length && currentStepIndex >= currentStepPlan.steps.length) {
            setCurrentStepIndex(0);
        }
    }, [currentStepIndex, currentStepPlan]);

    useEffect(() => {
        if (isEmbedded && window.parent && currentStep) {
            window.parent.postMessage({
                type: 'TUTORIAL_STEP_CHANGED',
                step: currentStep,
                steps: currentStepPlan?.steps || [],
                studentTutorialId: studentTutorialId
            }, '*');
        }
    }, [currentStep, currentStepPlan, isEmbedded, studentTutorialId]);

    useEffect(() => {
        setHasStartedSharedTutorial(!isSharedAccess);
    }, [isSharedAccess, shareToken]);

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
            setViewportHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const increaseFontSize = () => {
        setFontScale((prev) => {
            const next = Math.min(1.5, Number((prev + 0.1).toFixed(2)));
            if (next !== prev) trackInteraction('font_size_increase', { from: prev, to: next });
            return next;
        });
    };

    const decreaseFontSize = () => {
        setFontScale((prev) => {
            const next = Math.max(0.8, Number((prev - 0.1).toFixed(2)));
            if (next !== prev) trackInteraction('font_size_decrease', { from: prev, to: next });
            return next;
        });
    };

    const resetFontSize = () => {
        setFontScale((prev) => {
            if (prev !== 1) trackInteraction('font_size_reset', { from: prev, to: 1 });
            return 1;
        });
    };

    const socketRef = useRef();
    const interactionLog = useRef([]);
    const clientSessionId = useRef('');

    const sharedSessionStorageKey = useMemo(() => (
        isSharedAccess && shareToken ? `shared-tutorial-session-${shareToken}` : ''
    ), [isSharedAccess, shareToken]);

    const getOrCreateSharedSessionId = useCallback(() => {
        if (!isSharedAccess || !sharedSessionStorageKey) {
            if (!clientSessionId.current) {
                clientSessionId.current = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            return clientSessionId.current;
        }

        const existing = window.localStorage.getItem(sharedSessionStorageKey);
        if (existing) {
            clientSessionId.current = existing;
            return existing;
        }

        const created = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        window.localStorage.setItem(sharedSessionStorageKey, created);
        clientSessionId.current = created;
        return created;
    }, [isSharedAccess, sharedSessionStorageKey]);


    useEffect(() => {
        getOrCreateSharedSessionId();
    }, [getOrCreateSharedSessionId]);

    // +++ UPDATED: Determine Exit Path +++
    const exitPath = location.state?.returnTo || (studentTutorial ? `/homework/${studentTutorial.homeworkId._id}` : '/dashboard');

    const getFullUrl = useCallback((path) => {
        if (!path) return '';
        return path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
    }, []);

    const isAudioEnabled = () => {
        if (isPreviewAccess) return true;
        if (!studentTutorial || !studentTutorial.studentId) return false;
        if (typeof studentTutorial.studentId === 'object') {
            return studentTutorial.studentId.audioNarrationEnabled === true;
        }
        return false;
    };

    const getDefaultAudioSpeed = () => {
        if (!studentTutorial || !studentTutorial.studentId || typeof studentTutorial.studentId !== 'object') return 1;
        const rawSpeed = Number(studentTutorial.studentId.audioNarrationSpeed);
        return [0.5, 0.75, 1].includes(rawSpeed) ? rawSpeed : 1;
    };

    const defaultAudioSpeed = getDefaultAudioSpeed();

    const trackInteraction = (action, details = {}) => {
        let derivedStepName = `STEP_${currentStepIndex}`;
        if (studentTutorial) {
            const preferredKeyTerms = resolvePreferredKeyTermsPerPage(studentTutorial);
            const chunk = studentTutorial.generatedContent?.[currentChunkIndex] || null;
            const panels = buildGlossaryPanels(chunk?.glossary || [], preferredKeyTerms);
            const hasSeq = Boolean(chunk?.sequencing?.steps?.length > 0);
            const hasCat = Boolean(chunk?.categorisation?.itemsA?.length > 0);
            const plan = filterStepPlanByEnabledSteps(buildAdaptiveStepPlan(panels.length, preferredKeyTerms, hasSeq, hasCat), enabledPreviewSteps);
            derivedStepName = plan.steps[currentStepIndex] || derivedStepName;
        }

        const timestamp = new Date().toISOString();
        const payload = {
            timestamp,
            studentId: studentTutorial?.studentId?._id || studentTutorial?.studentId,
            tutorialId: studentTutorialId,
            chunkIndex: currentChunkIndex,
            step: derivedStepName,
            action,
            details
        };
        interactionLog.current.push(payload);
        if (socketRef.current) {
            socketRef.current.emit('student-action', payload);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');
                let tutorialData;
                let slideshowData;

                if (isSharedAccess) {
                    const publicRes = await studentTutorialLinkAPI.getPublic(shareToken);
                    tutorialData = publicRes.data?.tutorial;
                    const publicFollowOnTask = String(publicRes.data?.followOnTask || '').trim();
                    if (tutorialData && publicFollowOnTask && !String(tutorialData.followOnTask || '').trim()) {
                        tutorialData = { ...tutorialData, followOnTask: publicFollowOnTask };
                    }
                    slideshowData = publicRes.data?.slideshows || [];
                } else {
                    const tutorialRes = await studentTutorialAPI.getById(studentTutorialId);
                    tutorialData = tutorialRes.data;
                    const slideshowsRes = await studentSlideshowAPI.getForStudentTutorial(studentTutorialId);
                    slideshowData = slideshowsRes.data || [];
                }

                if (!tutorialData || !tutorialData.studentId) throw new Error('Failed to load tutorial data.');
                if (tutorialData.status !== 'COMPLETED') throw new Error('This tutorial content is not yet completed.');

                setStudentTutorial(tutorialData);
                setAttempt({ answers: [] });
                setAllSlideshows(new Map(slideshowData.map(s => [s.chunkIndex, s])));
                
            } catch (err) {
                console.error("Error fetching tutorial data:", err);
                setError(err.message || 'Failed to load tutorial data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [studentTutorialId, isSharedAccess, shareToken]);

    useEffect(() => {
        if (!studentTutorial || isSharedAccess || isPreviewAccess) return; 

        // +++ FIX: Reliable Socket Connection +++
        // 1. Strip the '/api' suffix if present to find the root URL
        const rawUrl = SOCKET_SERVER_URL.replace(/\/api\/?$/, '');
        
        // 2. Connect with robust options
        socketRef.current = io(rawUrl, {
            transports: ['websocket', 'polling'], // Allow fallback
            reconnectionAttempts: 5,
            auth: socketAuth // teacher's ID token: only the tutorial's owner may join its room
        });

        socketRef.current.emit('join-tutorial-room', studentTutorialId);
        
        socketRef.current.on('new-answer', (newAnswerData) => {
            const sId = studentTutorial.studentId._id || studentTutorial.studentId;

            // Track only events for this student and this browser session.
            if (
                String(newAnswerData.studentId) === String(sId) &&
                String(newAnswerData.clientSessionId) === String(clientSessionId.current)
            ) {
                setAttempt((prevAttempt) => {
                    const safePrev = prevAttempt || { answers: [] };
                    const updatedAnswers = [...safePrev.answers, newAnswerData.answer];
                    return { ...safePrev, answers: updatedAnswers };
                });
            }
        });
        return () => { if (socketRef.current) socketRef.current.disconnect(); };
    }, [studentTutorialId, studentTutorial, isSharedAccess, isPreviewAccess]); 

    useEffect(() => {
        if (explainerAudioRef.current) {
            explainerAudioRef.current.playbackRate = explainerSpeed;
        }
    }, [explainerSpeed]);

    useEffect(() => {
        setExplainerSpeed(defaultAudioSpeed);
    }, [defaultAudioSpeed, currentChunkIndex, currentStepIndex]);

    useEffect(() => {
        if (isComplete) {
            const finalizeAttempt = async () => {
                try {
                    if (!isSharedAccess && !isPreviewAccess) {
                        await axios.post(`${API_BASE_URL}/api/tutorial-attempts/logs`, {
                            studentTutorialId,
                            studentId: studentTutorial?.studentId?._id || studentTutorial?.studentId,
                            clientSessionId: getOrCreateSharedSessionId(),
                            logs: interactionLog.current
                        });
                    }

                    if (studentTutorial?.studentId?._id || studentTutorial?.studentId) {
                        if (isSharedAccess) {
                            await studentTutorialLinkAPI.completePublicAttempt(shareToken, {
                                clientSessionId: getOrCreateSharedSessionId(),
                                jcqExtendedResponse
                            });
                        } else if (!isPreviewAccess) {
                            await tutorialAttemptAPI.completeAttempt({
                                studentId: studentTutorial.studentId._id || studentTutorial.studentId,
                                studentTutorialId,
                                clientSessionId: getOrCreateSharedSessionId(),
                                jcqExtendedResponse
                            });
                        }
                    }

                    if (isSharedAccess && sharedSessionStorageKey) {
                        window.localStorage.removeItem(sharedSessionStorageKey);
                        clientSessionId.current = '';
                    }

                    console.log("Attempt completion saved successfully");
                } catch (e) {
                    console.error("Failed to finalize tutorial attempt:", e);
                }
            };
            finalizeAttempt();
        }
    }, [isComplete, studentTutorialId, studentTutorial, isSharedAccess, shareToken, isPreviewAccess, sharedSessionStorageKey, getOrCreateSharedSessionId, jcqExtendedResponse]);


    const handleStartSharedTutorial = () => {
        getOrCreateSharedSessionId();
        setHasStartedSharedTutorial(true);
    };

    const advanceToNextStep = () => {
        if (explainerAudioRef.current) {
            explainerAudioRef.current.pause();
            setExplainerPlaying(false);
        }

        if (!studentTutorial) return;
        const currentChunkData = studentTutorial.generatedContent?.[currentChunkIndex] || null;
        const preferredKeyTerms = resolvePreferredKeyTermsPerPage(studentTutorial);
        const currentPanels = buildGlossaryPanels(currentChunkData?.glossary || [], preferredKeyTerms);
        const currentHasSequencing = tutorialHasSequencing(studentTutorial?.generatedContent || []);
        const currentHasCategorisation = tutorialHasCategorisation(studentTutorial?.generatedContent || []);
        const currentStepPlan = filterStepPlanByEnabledSteps(buildAdaptiveStepPlan(currentPanels.length, preferredKeyTerms, currentHasSequencing, currentHasCategorisation), enabledPreviewSteps);
        const maxStepIndex = currentStepPlan.steps.length - 1;
        const maxChunkIndex = studentTutorial.generatedContent.length - 1;

        let nextStepIdx = currentStepIndex + 1;
        let nextChunkIdx = currentChunkIndex;
        
        if (nextStepIdx > maxStepIndex) {
            nextStepIdx = 0;
            nextChunkIdx = currentChunkIndex + 1;
            setGlobalUsedQuestionIds(new Set());
        }

        if (nextChunkIdx > maxChunkIndex) {
            if (shouldCollectJcqExtendedWriting && !jcqExtendedResponse) {
                setIsExtendedWritingActive(true);
                return;
            }
            setIsComplete(true);
            return;
        }

        const nextChunk = studentTutorial.generatedContent[nextChunkIdx];
        const nextPanels = buildGlossaryPanels(nextChunk?.glossary || [], preferredKeyTerms);
        const nextHasSequencing = Boolean(nextChunk?.sequencing?.steps?.length > 0);
        const nextHasCategorisation = Boolean(nextChunk?.categorisation?.itemsA?.length > 0);
        const nextStepPlan = filterStepPlanByEnabledSteps(buildAdaptiveStepPlan(nextPanels.length, preferredKeyTerms, nextHasSequencing, nextHasCategorisation), enabledPreviewSteps);
        const nextStepName = nextStepPlan.steps[nextStepIdx] || currentStepPlan.steps[nextStepIdx] || 'QUIZ_1';
        const hasGlossary = nextPanels.length > 0;
        
        if (nextStepName?.startsWith('GLOSSARY_') && !hasGlossary) {
            nextStepIdx++;
             if (nextStepIdx > maxStepIndex) {
                nextStepIdx = 0;
                nextChunkIdx++;
            }
            if (nextChunkIdx > maxChunkIndex) {
                setIsComplete(true);
                return;
            }
        }

        setCurrentStepIndex(nextStepIdx);
        setCurrentChunkIndex(nextChunkIdx);
    };

    const handleExtendedWritingComplete = (response) => {
        setJcqExtendedResponse(response);
        setIsExtendedWritingActive(false);
        setIsComplete(true);
    };

    const handleAnswerSubmit = async (answerData, questionId) => {
        if (!studentTutorial) return;
        setGlobalUsedQuestionIds(prev => new Set(prev).add(questionId));
        const payload = {
            studentId: studentTutorial.studentId._id || studentTutorial.studentId,
            studentTutorialId: studentTutorial._id,
            lessonPlanId: studentTutorial.lessonPlan,
            clientSessionId: getOrCreateSharedSessionId(),
            answer: answerData
        };
        try {
            if (isPreviewAccess) {
                setAttempt((prevAttempt) => {
                    const safePrev = prevAttempt || { answers: [] };
                    return {
                        ...safePrev,
                        answers: [...safePrev.answers, answerData]
                    };
                });
                return { data: { previewOnly: true } };
            }

            if (isSharedAccess) {
                const response = await studentTutorialLinkAPI.savePublicAttemptAnswer(shareToken, {
                    answer: answerData,
                    clientSessionId: getOrCreateSharedSessionId()
                });
                setAttempt((prevAttempt) => {
                    const safePrev = prevAttempt || { answers: [] };
                    const latestAnswer = response?.data?.answers?.[response.data.answers.length - 1];

                    if (!latestAnswer) {
                        return {
                            ...safePrev,
                            answers: [...safePrev.answers, answerData]
                        };
                    }

                    return {
                        ...safePrev,
                        answers: [...safePrev.answers, latestAnswer]
                    };
                });
                return response;
            }
            return await tutorialAttemptAPI.saveTutorialAnswer(payload);
        }
        catch (error) { console.error("Failed to save tutorial progress:", error); }
    };

    const handleGlossaryResult = (termObj, tries, duration) => {
        const answerPayload = {
            questionIndex: 0,
            questionText: `Match: ${termObj.term}`,
            studentAnswer: `Matched in ${tries} ${tries === 1 ? 'try' : 'tries'}`,
            correctAnswer: termObj.definition,
            isCorrect: true,
            difficulty: 'glossary',
            timeTaken: duration
        };
        // +++ FIX: Return the promise +++
        return handleAnswerSubmit(answerPayload, `glossary-${termObj.term}-${Date.now()}`);
    };

    const handleFillGapsResult = ({ correctCount, total, duration }) => {
        const answerPayload = {
            questionIndex: 0,
            questionText: 'Fill in the Gaps: Narrative Explainer',
            studentAnswer: `${correctCount} of ${total} gaps correct`,
            correctAnswer: `All ${total} gaps filled correctly`,
            isCorrect: correctCount === total,
            difficulty: 'fill_in_gaps',
            timeTaken: duration
        };
        return handleAnswerSubmit(answerPayload, `fill-gaps-${currentChunkIndex}-${Date.now()}`);
    };

    const handleSequencingResult = ({ correctCount, total, duration }) => {
        const rawChunk = studentTutorial?.generatedContent?.[currentChunkIndex];
        const seqTitle = rawChunk?.sequencing?.title || 'Put the steps in order';
        const answerPayload = {
            questionIndex: 0,
            questionText: `Sequencing: ${seqTitle}`,
            studentAnswer: `${correctCount} of ${total} steps in correct position`,
            correctAnswer: `All ${total} steps in correct order`,
            isCorrect: correctCount === total,
            difficulty: 'sequencing',
            timeTaken: duration
        };
        return handleAnswerSubmit(answerPayload, `sequencing-${currentChunkIndex}-${Date.now()}`);
    };

    const handleCategorisationResult = ({ correctCount, total, duration }) => {
        const rawChunk = studentTutorial?.generatedContent?.[currentChunkIndex];
        const catTitle = `${rawChunk?.categorisation?.categoryA || 'A'} vs ${rawChunk?.categorisation?.categoryB || 'B'}`;
        const answerPayload = {
            questionIndex: 0,
            questionText: `Categorisation: ${catTitle}`,
            studentAnswer: `${correctCount} of ${total} items correctly sorted`,
            correctAnswer: `All ${total} items in correct category`,
            isCorrect: correctCount === total,
            difficulty: 'categorisation',
            timeTaken: duration
        };
        return handleAnswerSubmit(answerPayload, `categorisation-${currentChunkIndex}-${Date.now()}`);
    };

    const toggleExplainerAudio = () => {
        trackInteraction(explainerPlaying ? 'audio_pause' : 'audio_play');
        if (explainerAudioRef.current) {
            if (explainerPlaying) {
                explainerAudioRef.current.pause();
                setExplainerPlaying(false);
            } else {
                explainerAudioRef.current.playbackRate = explainerSpeed; 
                explainerAudioRef.current.play().catch(e => console.error(e));
                setExplainerPlaying(true);
            }
        }
    };

    const cycleExplainerSpeed = () => {
        const speeds = [0.5, 0.75, 1];
        const nextIdx = (speeds.indexOf(explainerSpeed) + 1) % speeds.length;
        trackInteraction('audio_speed_change', { from: explainerSpeed, to: speeds[nextIdx] });
        setExplainerSpeed(speeds[nextIdx]);
    };

    const handleToggleReviewExplainer = () => {
        trackInteraction(isReviewingExplainer ? 'close_explainer_review' : 'open_explainer_review');
        setIsReviewingExplainer(!isReviewingExplainer);
    };

    if (loading) return <div style={styles.pageContainer}>Loading Tutorial Preview...</div>;
    if (error) return <div style={styles.pageContainer}><p style={styles.errorText}>{error}</p><Link to="/dashboard">Back</Link></div>;
    
    const studentName = studentTutorial.studentId?.name || studentTutorial.studentName || 'Unknown';
    const lessonNumber = studentTutorial?.homeworkId?.lessonNumber;
    const tutorialNumber = studentTutorial?.homeworkId?.homeworkNumber;
    const tutorialTitle = (Number.isFinite(Number(lessonNumber)) && Number.isFinite(Number(tutorialNumber)))
        ? `Lesson ${lessonNumber}: Tutorial ${tutorialNumber}`
        : 'Tutorial Preview';
    const preferredKeyTermsPerPage = resolvePreferredKeyTermsPerPage(studentTutorial);
    const currentSlideshow = allSlideshows.get(currentChunkIndex);

    const isForeignLanguageContent = ((studentTutorial.contentLanguage || 'English').toLowerCase() !== 'english');
    const glossaryLanguageMode = studentTutorial.glossaryLanguageMode || (isForeignLanguageContent ? 'PRIMARY_TEXT_ENGLISH_GLOSSARY' : 'ENGLISH_TEXT_PRIMARY_GLOSSARY');
    const hasReferenceTranslations = Array.isArray(studentTutorial.englishGeneratedContent) && studentTutorial.englishGeneratedContent.length > 0;
    const hoverTranslationsEnabled = Boolean(studentTutorial?.translationApplied && hasReferenceTranslations);

    const sourceChunks = Array.isArray(studentTutorial.generatedContent) ? studentTutorial.generatedContent : [];
    const referenceChunks = Array.isArray(studentTutorial.englishGeneratedContent) ? studentTutorial.englishGeneratedContent : [];
    const showEnglishAsPrimary = glossaryLanguageMode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY';
    const primaryLanguageLabel = studentTutorial?.studentId?.primaryLanguage || studentTutorial.contentLanguage || 'Primary language';
    const translationHintLanguage = showEnglishAsPrimary
        ? primaryLanguageLabel
        : 'English';

    const currentChunk = showEnglishAsPrimary && hasReferenceTranslations
        ? referenceChunks[currentChunkIndex]
        : sourceChunks[currentChunkIndex];

    const currentEnglishChunk = showEnglishAsPrimary && hasReferenceTranslations
        ? sourceChunks[currentChunkIndex]
        : referenceChunks[currentChunkIndex];
    const sequencingChunkIndex = sourceChunks.findIndex((chunk) => Array.isArray(chunk?.sequencing?.steps) && chunk.sequencing.steps.length > 0);
    const categorisationChunkIndex = sourceChunks.findIndex((chunk) => (
        (Array.isArray(chunk?.categorisation?.itemsA) && chunk.categorisation.itemsA.length > 0)
        || (Array.isArray(chunk?.categorisation?.itemsB) && chunk.categorisation.itemsB.length > 0)
    ));
    const activeSequencingChunkIndex = sequencingChunkIndex >= 0 ? sequencingChunkIndex : currentChunkIndex;
    const activeCategorisationChunkIndex = categorisationChunkIndex >= 0 ? categorisationChunkIndex : currentChunkIndex;
    const sequenceChunk = showEnglishAsPrimary && hasReferenceTranslations
        ? referenceChunks[activeSequencingChunkIndex]
        : sourceChunks[activeSequencingChunkIndex];
    const sequenceEnglishChunk = showEnglishAsPrimary && hasReferenceTranslations
        ? sourceChunks[activeSequencingChunkIndex]
        : referenceChunks[activeSequencingChunkIndex];
    const categorisationChunk = showEnglishAsPrimary && hasReferenceTranslations
        ? referenceChunks[activeCategorisationChunkIndex]
        : sourceChunks[activeCategorisationChunkIndex];
    const categorisationEnglishChunk = showEnglishAsPrimary && hasReferenceTranslations
        ? sourceChunks[activeCategorisationChunkIndex]
        : referenceChunks[activeCategorisationChunkIndex];

    const combinedGlossary = !isComplete ? (currentChunk?.glossary || []).map((item, index) => ({
        ...item,
        translationTerm: currentEnglishChunk?.glossary?.[index]?.term || '',
        translationDefinition: currentEnglishChunk?.glossary?.[index]?.definition || ''
    })) : [];

    const glossaryTranslationPairs = combinedGlossary
        .filter(item => item.term && item.translationTerm)
        .map(item => ({ term: item.term, translation: item.translationTerm }));

    const slideshowCaptionTranslationPairs = glossaryTranslationPairs;
    const slideshowHoverTranslationsEnabled = Boolean(studentTutorial?.translationApplied && slideshowCaptionTranslationPairs.length > 0);

    const mcqQuestionTranslationPairs = !isComplete
        ? ['easy', 'medium', 'hard'].flatMap((difficulty) => {
            const translatedMcqs = currentChunk?.mcqs?.[difficulty] || [];
            const englishMcqs = currentEnglishChunk?.mcqs?.[difficulty] || [];
            return translatedMcqs.map((mcq, index) => ({
                term: mcq?.question || '',
                translation: englishMcqs?.[index]?.question || ''
            })).filter(pair => pair.term && pair.translation);
        })
        : [];

    const mcqOptionTranslationPairs = !isComplete
        ? ['easy', 'medium', 'hard'].flatMap((difficulty) => {
            const translatedMcqs = currentChunk?.mcqs?.[difficulty] || [];
            const englishMcqs = currentEnglishChunk?.mcqs?.[difficulty] || [];

            return translatedMcqs.flatMap((mcq, index) => {
                const translatedOptions = mcq?.options || {};
                const englishOptions = englishMcqs?.[index]?.options || {};

                return ['A', 'B', 'C', 'D'].map((key) => ({
                    term: translatedOptions[key] || '',
                    translation: englishOptions[key] || ''
                })).filter(pair => pair.term && pair.translation);
            });
        })
        : [];

    const allMcqs = !isComplete
        ? ['easy', 'medium', 'hard'].flatMap((difficulty) => {
            const difficultyQuestions = currentChunk?.mcqs?.[difficulty] || [];
            const englishDifficultyQuestions = currentEnglishChunk?.mcqs?.[difficulty] || [];
            return difficultyQuestions.map((mcq, sourceQuestionIndex) => ({
                ...mcq,
                sourceQuestionIndex,
                objectiveFocusCanonical: (englishDifficultyQuestions?.[sourceQuestionIndex]?.objectiveFocus || mcq?.objectiveFocus || '').toString().trim()
            }));
        })
        : [];
    
    const glossaryPanels = !isComplete ? buildGlossaryPanels(combinedGlossary, preferredKeyTermsPerPage) : [];

    const englishAnswerLookup = (() => {
        const lookup = new Map();

        const translatedChunks = studentTutorial.generatedContent || [];
        const englishChunks = studentTutorial.englishGeneratedContent || [];

        translatedChunks.forEach((translatedChunk, chunkIndex) => {
            const englishChunk = englishChunks[chunkIndex];
            if (!translatedChunk || !englishChunk) return;

            const translatedGlossary = translatedChunk.glossary || [];
            const englishGlossary = englishChunk.glossary || [];
            translatedGlossary.forEach((translatedItem, glossaryIndex) => {
                const englishItem = englishGlossary[glossaryIndex];
                if (!translatedItem?.term || !englishItem?.term) return;
                lookup.set(`match:${translatedItem.term.toLowerCase()}`, {
                    questionText: `Match: ${englishItem.term}`,
                    correctAnswer: englishItem.definition || ''
                });
            });

            ['easy', 'medium', 'hard'].forEach((difficulty) => {
                const translatedMcqs = translatedChunk?.mcqs?.[difficulty] || [];
                const englishMcqs = englishChunk?.mcqs?.[difficulty] || [];

                translatedMcqs.forEach((translatedMcq, questionIndex) => {
                    const englishMcq = englishMcqs[questionIndex];
                    if (!translatedMcq?.question || !englishMcq?.question) return;

                    const optionLookup = new Map();
                    ['A', 'B', 'C', 'D'].forEach((key) => {
                        const translatedOption = translatedMcq?.options?.[key];
                        const englishOption = englishMcq?.options?.[key];
                        if (translatedOption && englishOption) {
                            optionLookup.set(String(translatedOption), String(englishOption));
                        }
                    });

                    lookup.set(`mcq:${translatedMcq.question.toLowerCase()}`, {
                        questionText: englishMcq.question,
                        correctAnswer: englishMcq?.options?.[englishMcq.correctAnswer] || englishMcq.correctAnswer,
                        studentAnswerLookup: optionLookup
                    });
                });
            });
        });

        return lookup;
    })();

    const toEnglishAnswer = (answer = {}) => {
        if (!isForeignLanguageContent || !studentTutorial.translationApplied) return answer;

        const safeQuestion = String(answer.questionText || '');
        if (safeQuestion.startsWith('Match:')) {
            const translatedTerm = safeQuestion.replace('Match:', '').trim().toLowerCase();
            const lookup = englishAnswerLookup.get(`match:${translatedTerm}`);
            if (!lookup) return answer;
            return {
                ...answer,
                questionText: lookup.questionText,
                correctAnswer: lookup.correctAnswer || answer.correctAnswer
            };
        }

        const mcqLookup = englishAnswerLookup.get(`mcq:${safeQuestion.toLowerCase()}`);
        if (!mcqLookup) return answer;

        return {
            ...answer,
            questionText: mcqLookup.questionText,
            studentAnswer: mcqLookup.studentAnswerLookup?.get(String(answer.studentAnswer || '')) || answer.studentAnswer,
            correctAnswer: mcqLookup.correctAnswer || answer.correctAnswer
        };
    };

    const allAnswers = (attempt ? attempt.answers : []).map(toEnglishAnswer);
    const totalAnswersLogged = allAnswers.length;
    const glossaryAnswers = allAnswers.filter(a => a.questionText && a.questionText.startsWith('Match:'));
    const contentAnswers = allAnswers.filter(a => !a.questionText || !a.questionText.startsWith('Match:'));
    const totalContent = contentAnswers.length;
    const correctContent = contentAnswers.filter(a => a.isCorrect).length;
    const substantiveRate = totalContent > 0 ? Math.round((correctContent / totalContent) * 100) : 0;
    const totalGlossary = glossaryAnswers.length;
    const firstTryGlossary = glossaryAnswers.filter(a => a.studentAnswer && a.studentAnswer.includes('Matched in 1 try')).length;
    const keyTermsRate = totalGlossary > 0 ? Math.round((firstTryGlossary / totalGlossary) * 100) : 0;
    const followOnTask = String(studentTutorial?.followOnTask || studentTutorial?.reviewRejectionReason || '').trim();

    const previewFallbackAttempt = {
        answers: allAnswers,
        status: 'completed',
        startedAt: attempt?.startedAt || new Date().toISOString(),
        completedAt: attempt?.completedAt || new Date().toISOString()
    };

    const renderCompletionScreen = () => {
        const goodCandidates = allAnswers.filter(a => a.questionText.startsWith('Match:') ? a.studentAnswer.includes('Matched in 1 try') : a.isCorrect === true);
        const reviewCandidates = allAnswers.filter(a => a.questionText.startsWith('Match:') ? !a.studentAnswer.includes('Matched in 1 try') : a.isCorrect === false);
        const goodItem = goodCandidates.length > 0 ? goodCandidates[Math.floor(Math.random() * goodCandidates.length)] : null;
        const reviewItem = reviewCandidates.length > 0 ? reviewCandidates[Math.floor(Math.random() * reviewCandidates.length)] : null;

        const reportLink = !isSharedAccess && (
            <div style={{ textAlign: 'center', marginTop: '28px' }}>
                <Link
                    to={`/tutorial-report/${studentTutorialId}`}
                    state={{ returnTo: exitPath, fallbackAttempt: previewFallbackAttempt }}
                    onClick={() => {
                        if (isPreviewAccess && studentTutorialId && previewFallbackAttempt.answers.length > 0) {
                            try { sessionStorage.setItem(`preview-report-attempt-${studentTutorialId}`, JSON.stringify(previewFallbackAttempt)); } catch {}
                        }
                    }}
                    className="btn-base"
                    style={{ background: BRAND.coral, color: 'white' }}
                >
                    View Full Report →
                </Link>
            </div>
        );

        const highlights = (
            <>
                {goodItem && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '800', color: BRAND.teal, fontFamily: 'Nunito', fontSize: '0.95em', marginBottom: '6px' }}>🌟 You Nailed This:</div>
                        <div style={{ background: '#E0F2F1', padding: '12px 16px', borderRadius: '10px', borderLeft: `5px solid ${BRAND.teal}` }}>
                            <div style={{ fontWeight: '700', fontFamily: 'Inter', fontSize: '0.9em' }}><RenderMarkdown inline>{goodItem.questionText.replace('Match: ', '')}</RenderMarkdown></div>
                            <div style={{ marginTop: '4px', fontFamily: 'Inter', fontSize: '0.85em', color: '#444' }}>
                                {goodItem.questionText.startsWith('Match:') ? <RenderMarkdown inline>{goodItem.correctAnswer}</RenderMarkdown> : <span>Answer: <strong><RenderMarkdown inline>{goodItem.studentAnswer}</RenderMarkdown></strong></span>}
                            </div>
                        </div>
                    </div>
                )}
                {reviewItem && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '800', color: BRAND.warning, fontFamily: 'Nunito', fontSize: '0.95em', marginBottom: '6px' }}>💡 Keep Practising:</div>
                        <div style={{ background: '#FFF8E1', padding: '12px 16px', borderRadius: '10px', borderLeft: `5px solid ${BRAND.warning}` }}>
                            <div style={{ fontWeight: '700', fontFamily: 'Inter', fontSize: '0.9em' }}><RenderMarkdown inline>{reviewItem.questionText.replace('Match: ', '')}</RenderMarkdown></div>
                            <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#666', fontFamily: 'Inter', fontSize: '0.85em' }}><RenderMarkdown inline>{reviewItem.correctAnswer}</RenderMarkdown></div>
                        </div>
                    </div>
                )}
                {followOnTask && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '800', color: BRAND.charcoal, fontFamily: 'Nunito', fontSize: '0.95em', marginBottom: '6px' }}>📝 Follow-on Task</div>
                        <div style={{ background: '#F9FAFB', padding: '12px 16px', borderRadius: '10px', borderLeft: `5px solid ${BRAND.charcoal}` }}>
                            <div style={{ fontFamily: 'Inter', fontSize: '0.88em', whiteSpace: 'pre-wrap' }}><RenderMarkdown inline>{followOnTask}</RenderMarkdown></div>
                        </div>
                    </div>
                )}
            </>
        );

        // Score screen
        if (studentTutorial?.showScoreEnabled) {
            // Activity breakdown
            const mcqAnswers = allAnswers.filter(a => ['Easy', 'Medium', 'Hard'].includes(a.difficulty));
            const keyTermAnswers = allAnswers.filter(a => a.questionText?.startsWith('Match:'));
            const seqAnswers = allAnswers.filter(a => a.difficulty === 'sequencing');
            const catAnswers = allAnswers.filter(a => a.difficulty === 'categorisation');
            const gapAnswers = allAnswers.filter(a => a.difficulty === 'fill_in_gaps');

            const mcqRate = mcqAnswers.length > 0 ? Math.round((mcqAnswers.filter(a => a.isCorrect).length / mcqAnswers.length) * 100) : null;
            const keyTermRate = keyTermAnswers.length > 0 ? Math.round((keyTermAnswers.filter(a => a.studentAnswer?.includes('Matched in 1 try')).length / keyTermAnswers.length) * 100) : null;
            const seqRate = seqAnswers.length > 0 ? Math.round((seqAnswers.filter(a => a.isCorrect).length / seqAnswers.length) * 100) : null;
            const catRate = catAnswers.length > 0 ? Math.round((catAnswers.filter(a => a.isCorrect).length / catAnswers.length) * 100) : null;
            const gapRate = gapAnswers.length > 0 ? Math.round((gapAnswers.filter(a => a.isCorrect).length / gapAnswers.length) * 100) : null;

            const rateColor = (r) => r === null ? BRAND.charcoal : r >= 80 ? BRAND.success : r >= 50 ? BRAND.warning : BRAND.danger;

            const activities = [
                { label: 'Questions', emoji: '✅', rate: mcqRate, count: mcqAnswers.length },
                { label: 'Key Terms', emoji: '🔑', rate: keyTermRate, count: keyTermAnswers.length },
                { label: 'Sequencing', emoji: '🔢', rate: seqRate, count: seqAnswers.length },
                { label: 'Categories', emoji: '🗂️', rate: catRate, count: catAnswers.length },
                { label: 'Fill-in-Gaps', emoji: '✏️', rate: gapRate, count: gapAnswers.length },
            ].filter(a => a.count > 0);

            // Overall: weighted across all scored activities
            const allScoredAnswers = allAnswers.filter(a => a.isCorrect !== undefined);
            const overallCorrect = allScoredAnswers.filter(a => a.isCorrect || a.studentAnswer?.includes('Matched in 1 try')).length;
            const overallRate = allScoredAnswers.length > 0 ? Math.round((overallCorrect / allScoredAnswers.length) * 100) : null;

            return (
                <div style={{ maxWidth: '800px', margin: '0 auto', paddingTop: '20px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                        <h2 style={{ color: BRAND.success, fontSize: '2.8em', fontFamily: 'Nunito', fontWeight: '800', margin: '0 0 4px' }}>🎉 You Finished!</h2>
                        {studentName && <p style={{ margin: 0, color: '#5D7375', fontWeight: '700', fontFamily: 'Inter', fontSize: '1em' }}>{studentName}</p>}
                    </div>

                    <div style={{ background: 'white', borderRadius: '20px', border: `3px solid ${BRAND.charcoal}`, boxShadow: '8px 8px 0px rgba(45,64,66,0.1)', padding: '28px 24px' }}>

                        {/* Overall score */}
                        {overallRate !== null && (
                            <div style={{ textAlign: 'center', marginBottom: '24px', paddingBottom: '20px', borderBottom: '2px dashed #E0E0E0' }}>
                                <div style={{ fontSize: '0.72em', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: '#888', marginBottom: '6px' }}>Overall Score</div>
                                <div style={{ fontSize: '3.2em', fontWeight: '800', fontFamily: 'Nunito', color: rateColor(overallRate), lineHeight: 1 }}>{overallRate}%</div>
                                <div style={{ fontSize: '0.82em', color: '#888', marginTop: '4px' }}>{allScoredAnswers.length} questions answered</div>
                            </div>
                        )}

                        {/* Activity breakdown */}
                        {activities.length > 0 && (
                            <div style={{ marginBottom: '22px' }}>
                                <div style={{ fontSize: '0.72em', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: '#888', marginBottom: '10px' }}>Activity Breakdown</div>
                                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(activities.length, 3)}, 1fr)`, gap: '8px' }}>
                                    {activities.map(({ label, emoji, rate, count }) => (
                                        <div key={label} style={{ background: '#F9FAFB', border: '1px solid #E0E0E0', borderRadius: '12px', padding: '12px 8px', textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.3em', marginBottom: '4px' }}>{emoji}</div>
                                            <div style={{ fontSize: '0.7em', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.4px', color: '#888', marginBottom: '4px' }}>{label}</div>
                                            {rate !== null
                                                ? <div style={{ fontSize: '1.5em', fontWeight: '800', fontFamily: 'Nunito', color: rateColor(rate) }}>{rate}%</div>
                                                : <div style={{ fontSize: '1.1em', fontWeight: '800', fontFamily: 'Nunito', color: BRAND.success }}>✓</div>
                                            }
                                            <div style={{ fontSize: '0.72em', color: '#aaa', marginTop: '2px' }}>{count} logged</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Highlights + follow-on */}
                        {highlights}
                        {reportLink}
                    </div>
                </div>
            );
        }

        // Default motivational screen (showScore off)
        return (
            <div style={{ marginTop: '40px', maxWidth: '800px', margin: '0 auto' }}>
                <h2 style={{ textAlign: 'center', color: BRAND.success, fontSize: '4em', fontFamily: 'Nunito', marginBottom: '30px', fontWeight: '800' }}>Great Job!</h2>
                <div style={{ background: 'white', padding: '40px', borderRadius: '20px', border: `3px solid ${BRAND.charcoal}`, boxShadow: '8px 8px 0px rgba(45,64,66,0.1)' }}>
                    {highlights}
                    {reportLink}
                </div>
            </div>
        );
    };

    // keep renderGreatJobScreen as alias so nothing else breaks
    const renderGreatJobScreen = renderCompletionScreen;

    if (isSharedAccess && !hasStartedSharedTutorial) {
        return (
            <div
                style={{
                    ...styles.pageContainer,
                    minHeight: isEmbedded ? 'unset' : '100dvh',
                    maxWidth: 'none',
                    width: isEmbedded ? '100%' : '100vw',
                    margin: 0,
                    background: `linear-gradient(180deg, ${BRAND.cream} 0%, #FFFFFF 100%)`,
                    padding: isEmbedded ? '20px' : (isMobileViewport ? '12px' : '20px'),
                    position: 'relative',
                    overflow: isEmbedded ? 'visible' : 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                className={tutorialFontClass}
            >
                <div
                    style={{
                        backgroundImage: `radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%)`,
                        backgroundSize: '60px 60px',
                        backgroundPosition: '0 0, 30px 30px',
                        opacity: 0.15,
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)'
                    }}
                />

                <div
                    style={{
                        ...styles.card,
                        width: '100%',
                        maxWidth: isMobileViewport ? 'min(100%, 420px)' : 'min(92vw, 620px)',
                        margin: '0 auto',
                        textAlign: 'center',
                        boxSizing: 'border-box',
                        padding: isMobileViewport ? '20px 14px' : '30px 24px',
                        background: 'white',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <img
                        src={classlabsLogo}
                        alt="ClassLabs"
                        style={{
                            height: isMobileViewport ? '54px' : '88px',
                            width: 'auto',
                            maxWidth: '100%',
                            margin: '0 auto 16px auto',
                            display: 'block'
                        }}
                    />

                    <h1 style={{ margin: '0 0 20px 0', fontFamily: 'Nunito, sans-serif', fontWeight: 800, color: BRAND.charcoal, fontSize: isMobileViewport ? '1.5rem' : '1.9rem' }}>
                        Ready to start your tutorial?
                    </h1>

                    <button
                        type="button"
                        onClick={handleStartSharedTutorial}
                        style={{
                            width: isMobileViewport ? '70%' : '50%',
                            minWidth: isMobileViewport ? '220px' : '260px',
                            maxWidth: '100%',
                            justifyContent: 'center',
                            background: BRAND.coral,
                            color: 'white',
                            padding: isMobileViewport ? '24px 20px' : '28px 28px',
                            fontSize: isMobileViewport ? '1rem' : '1.08rem',
                            borderRadius: '16px',
                            fontFamily: 'Nunito, sans-serif',
                            fontWeight: 800,
                            textDecoration: 'none',
                            transition: 'all 0.15s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            margin: '0 auto',
                            border: `3px solid ${BRAND.charcoal}`,
                            boxShadow: `4px 4px 0px ${BRAND.charcoal}`,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            cursor: 'pointer',
                            touchAction: 'manipulation'
                        }}
                    >
                        Start Tutorial
                    </button>
                </div>
            </div>
        );
    }

    return (
        // +++ UPDATED: Apply font class if check passes +++
        <div style={{ ...styles.splitScreenContainer, flexDirection: isMobileViewport ? 'column' : 'row' }} className={tutorialFontClass}>
            {/* ... styles ... */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');

                    @font-face {
                        font-family: 'OpenDyslexic';
                        src: url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Regular.woff') format('woff');
                        font-style: normal;
                        font-weight: 400;
                        font-display: swap;
                    }
                    @font-face {
                        font-family: 'OpenDyslexic';
                        src: url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Bold.woff') format('woff');
                        font-style: normal;
                        font-weight: 700;
                        font-display: swap;
                    }
                    @font-face {
                        font-family: 'OpenDyslexic';
                        src: url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Italic.woff') format('woff');
                        font-style: italic;
                        font-weight: 400;
                        font-display: swap;
                    }

                    .font-opendyslexic,
                    .font-opendyslexic * {
                        font-family: 'OpenDyslexic', 'Inter', sans-serif !important;
                    }

                    .font-nunito,
                    .font-nunito * {
                        font-family: 'Nunito', 'Inter', sans-serif !important;
                    }

                    .font-arial,
                    .font-arial * {
                        font-family: Arial, 'Inter', sans-serif !important;
                    }
                    
                    .hide-scrollbar::-webkit-scrollbar { display: none; }
                    .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 40px 40px;
                        background-position: 0 0, 20px 20px;
                        opacity: 0.08;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                        pointer-events: none;
                    }

                    .btn-base {
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                        border-radius: 10px;
                        padding: 10px 24px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.1s ease;
                        font-size: 1rem;
                        display: inline-flex;
                        align-items: center;
                        text-decoration: none;
                    }
                    .btn-base:active { transform: translateY(2px); border-bottom: 2px solid ${BRAND.charcoal}; margin-bottom: 2px; }
                    
                    .btn-exit {
                        background-color: ${BRAND.coral};
                        color: white;
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                        border-radius: 8px;
                        padding: 6px 16px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        font-size: 0.8em;
                        cursor: pointer;
                        text-decoration: none;
                        display: inline-block;
                        margin-top: 5px;
                        transition: all 0.1s;
                    }
                    .btn-exit:active { border-bottom: 2px solid ${BRAND.charcoal}; transform: translateY(2px); }
                    
                    @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
                `}
            </style>

            <div style={{ ...styles.playerPane, padding: isMobileViewport ? '14px' : styles.playerPane.padding }} className="hide-scrollbar">
                
                {!isSharedAccess && !isTrackerOpen && (
                    <button 
                        onClick={() => setIsTrackerOpen(true)}
                        style={{
                            position: 'absolute', right: 0, top: '145px', 
                            background: BRAND.teal, color: 'white', border: 'none',
                            borderRadius: '8px 0 0 8px', padding: '10px 15px',
                            cursor: 'pointer', zIndex: 10, fontFamily: 'Nunito',
                            fontWeight: 'bold', boxShadow: '-2px 2px 5px rgba(0,0,0,0.2)'
                        }}
                    >
                        ← Teacher Tracker
                    </button>
                )}

                <div className="pattern-bg"></div>

                {isReviewingExplainer && (
                    <div
                        className="tutorial-text"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(45, 64, 66, 0.60)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 20,
                            padding: '20px'
                        }}
                    >
                        <div style={{ ...styles.card, backgroundColor: bgTheme, width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px', borderBottom:`2px dashed #E0E0E0`, paddingBottom:'10px'}}>
                                <h3 style={{margin:0, fontFamily:'Nunito', fontWeight:'800', color: BRAND.charcoal}}>Review Explainer</h3>
                            </div>
                            <div style={{fontSize: `${1.15 * fontScale}em`, lineHeight: '1.7', fontFamily:'Inter', color: BRAND.charcoal}}>{hoverTranslationsEnabled ? <HoverableTranslationText text={currentChunk.adjustedExplainer} translationPairs={glossaryTranslationPairs} translationLabel={`${translationHintLanguage} translation`} /> : <ReactMarkdown>{stripMarkdownFormatting(currentChunk.adjustedExplainer)}</ReactMarkdown>}</div>
                            <div style={{textAlign:'right', marginTop:'20px'}}>
                                <button onClick={handleToggleReviewExplainer} className="btn-base" style={{background: BRAND.charcoal, color: 'white'}}>Return to Questions →</button>
                            </div>
                        </div>
                    </div>
                )}

                <LearningSafetyModal
                    isOpen={isLearningSafetyModalOpen}
                    onClose={() => setIsLearningSafetyModalOpen(false)}
                />


                {isSharedAccess && !shouldDockSafetyButtonBelowTutorial && (
                    <button
                        type="button"
                        onClick={() => setIsLearningSafetyModalOpen(true)}
                        className="btn-base"
                        style={{
                            position: 'fixed',
                            right: '22px',
                            bottom: '22px',
                            width: '58px',
                            height: '58px',
                            borderRadius: '50%',
                            padding: 0,
                            justifyContent: 'center',
                            background: BRAND.teal,
                            color: 'white',
                            boxShadow: '0 8px 18px rgba(45, 64, 66, 0.26)',
                            zIndex: 1200,
                            fontSize: '1.35rem'
                        }}
                        aria-label="Open learning safety information"
                        title="How we keep your learning safe"
                    >
                        🛡️
                    </button>
                )}
                
                <div style={{ 
                    maxWidth: isTrackerOpen ? '100%' : '800px', 
                    margin: '0 auto', position: 'relative', zIndex: 1, width: '100%' 
                }}>
                    <div style={{ ...styles.header, flexDirection: isMobileViewport ? 'column' : 'row', alignItems: isMobileViewport ? 'flex-start' : 'center', gap: isMobileViewport ? '12px' : 0, display: undefined, ...(isEmbedded ? { borderBottom: 'none', paddingBottom: 0, marginBottom: '14px' } : {}) }}>
                        {!isEmbedded && (
                        <div style={styles.headerTitle}>
                            <h2 style={{fontFamily:'Nunito', fontWeight:'800', color: BRAND.charcoal, margin:'0 0 5px 0'}}>
                                {tutorialTitle}
                            </h2>
                            <p style={{ margin: 0, color: '#5D7375', fontWeight: '700', fontFamily: 'Inter' }}>{studentName}</p>

                            {!isSharedAccess && !isComplete && (
                                <Link
                                    to={exitPath}
                                    onClick={() => trackInteraction('exit_preview')}
                                    className="btn-exit"
                                >
                                    EXIT PREVIEW
                                </Link>
                            )}
                        </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'stretch', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'white', border: `2px solid ${BRAND.charcoal}`, borderRadius: '10px', padding: '6px 8px' }}>
                                    <span style={{ fontSize: '0.75em', fontWeight: '800', color: BRAND.charcoal, fontFamily: 'Nunito', whiteSpace: 'nowrap', marginRight: '2px' }}>Reading Background</span>
                                    {[
                                        { color: BRAND.cream,     label: 'Cream' },
                                        { color: BRAND.paleBlue,  label: 'Pale Blue' },
                                        { color: BRAND.softPeach, label: 'Soft Peach' },
                                    ].map(({ color, label }) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => {
                                                setBgTheme((prev) => {
                                                    if (prev !== color) trackInteraction('reading_background_change', { from: prev, to: color, label });
                                                    return color;
                                                });
                                            }}
                                            aria-label={`Set background to ${label}`}
                                            style={{
                                                width: '22px', height: '22px', borderRadius: '50%',
                                                background: color, cursor: 'pointer', padding: 0,
                                                border: `2px solid ${bgTheme === color ? BRAND.charcoal : '#C0C0C0'}`,
                                                boxShadow: bgTheme === color ? `0 0 0 2px ${BRAND.teal}` : 'none',
                                                outline: 'none', transition: 'all 0.1s',
                                                flexShrink: 0,
                                            }}
                                        />
                                    ))}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'white', border: `2px solid ${BRAND.charcoal}`, borderRadius: '10px', padding: '6px 8px' }}>
                                    <button onClick={decreaseFontSize} className="btn-base" style={{ padding: '4px 10px', fontSize: '0.85em', background: '#F0F2F5', color: BRAND.charcoal }} aria-label="Decrease font size">A-</button>
                                    <span style={{ fontFamily: 'Nunito', fontWeight: '800', fontSize: '0.85em', color: BRAND.charcoal, minWidth: '36px', textAlign: 'center' }}>{Math.round(fontScale * 100)}%</span>
                                    <button onClick={increaseFontSize} className="btn-base" style={{ padding: '4px 10px', fontSize: '0.85em', background: BRAND.teal, color: 'white' }} aria-label="Increase font size">A+</button>
                                    <button onClick={resetFontSize} className="btn-base" style={{ padding: '4px 10px', fontSize: '0.75em', background: 'white', color: BRAND.charcoal }} aria-label="Reset font size">Reset</button>
                                </div>
                            </div>
                            {!isEmbedded && (
                            <div style={styles.timerWrapper}>
                                <CircularTimer durationInMinutes={studentTutorial?.homeworkId?.durationMinutes || 0} storageKey={`timer-${studentTutorialId}`} />
                            </div>
                            )}
                        </div>
                    </div>

                    <div style={{ ...styles.contentArea, fontSize: `${fontScale}em` }} className="tutorial-text">
                        {isComplete ? renderGreatJobScreen() : (isExtendedWritingActive || isWritingOnlyTest) ? (
                            <ExtendedWritingResponseSection
                                prompt={jcqExtendedWritingPrompt}
                                onComplete={handleExtendedWritingComplete}
                                onInteraction={trackInteraction}
                                bgTheme={bgTheme}
                            />
                        ) : (
                            <>
                                {currentStep === 'SLIDESHOW' && (
                                    <div onClickCapture={() => trackInteraction('slideshow_interaction')}>
                                        <EmbeddedSlideshow
                                            slideshowData={currentSlideshow}
                                            onSlideshowComplete={() => { trackInteraction('slideshow_complete'); advanceToNextStep(); }}
                                            audioEnabled={isAudioEnabled()}
                                            initialPlaybackSpeed={defaultAudioSpeed}
                                            enableHoverTranslations={slideshowHoverTranslationsEnabled}
                                            translationPairs={slideshowCaptionTranslationPairs}
                                            translationLabel={`${translationHintLanguage} translation`}
                                            bgTheme={bgTheme}
                                        />
                                    </div>
                                )}
                                        
                                {currentStep === 'EXPLAINER' && studentTutorial?.fillInGapsEnabled && currentChunk?.glossary?.length > 0 ? (
                                    <FillInGapsGame
                                        key={`fill-gaps-${currentChunkIndex}`}
                                        explainer={currentChunk.adjustedExplainer}
                                        glossary={currentChunk.glossary}
                                        onResult={handleFillGapsResult}
                                        onComplete={() => { trackInteraction('fill_gaps_complete'); advanceToNextStep(); }}
                                        onInteraction={trackInteraction}
                                        bgTheme={bgTheme}
                                        hoverTranslationsEnabled={hoverTranslationsEnabled}
                                        glossaryTranslationPairs={glossaryTranslationPairs}
                                        translationHintLanguage={translationHintLanguage}
                                    />
                                ) : currentStep === 'EXPLAINER' && (
                                    <div style={{...styles.card, backgroundColor: bgTheme}}>
                                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'15px', borderBottom:`2px dashed #E0E0E0`, paddingBottom:'10px'}}>
                                            <h3 style={{margin:0, fontFamily:'Nunito', fontWeight:'800', color: BRAND.charcoal}}>Narrative Explainer</h3>
                                            {currentChunk.adjustedExplainerAudioUrl && isAudioEnabled() && (
                                                <div style={{display:'flex', gap:'8px'}}>
                                                    <button onClick={toggleExplainerAudio} className="btn-base" style={{padding:'6px 12px', fontSize:'0.85em', background: BRAND.teal, color:'white'}}>{explainerPlaying ? 'Pause' : 'Play'}</button>
                                                    <button onClick={cycleExplainerSpeed} className="btn-base" style={{padding:'6px 12px', fontSize:'0.85em', background: 'white', color: BRAND.charcoal}}>{explainerSpeed}x</button>
                                                </div>
                                            )}
                                        </div>
                                        {currentChunk.adjustedExplainerAudioUrl && (
                                            <audio ref={explainerAudioRef} src={getFullUrl(currentChunk.adjustedExplainerAudioUrl)} style={{display:'none'}} />
                                        )}
                                        <div style={{fontSize: `${1.15 * fontScale}em`, lineHeight: '1.7', fontFamily:'Inter', color: BRAND.charcoal}}>{hoverTranslationsEnabled ? <HoverableTranslationText text={currentChunk.adjustedExplainer} translationPairs={glossaryTranslationPairs} translationLabel={`${translationHintLanguage} translation`} /> : <ReactMarkdown>{stripMarkdownFormatting(currentChunk.adjustedExplainer)}</ReactMarkdown>}</div>
                                        <div style={{textAlign:'right', marginTop:'20px'}}>
                                            <button onClick={() => { trackInteraction('explainer_continue'); advanceToNextStep(); }} className="btn-base" style={{background: BRAND.coral, color: 'white'}}>Continue →</button>
                                        </div>
                                    </div>
                                )}
                                        
                                {currentStep === 'SEQUENCE_1' && sequenceChunk?.sequencing?.steps?.length > 0 && (
                                    <SequencingGame
                                        key={`sequence-${activeSequencingChunkIndex}`}
                                        sequencing={sequenceChunk.sequencing}
                                        translationSteps={hoverTranslationsEnabled ? (sequenceEnglishChunk?.sequencing?.steps || []) : []}
                                        translationHintLanguage={translationHintLanguage}
                                        onResult={handleSequencingResult}
                                        onComplete={() => { trackInteraction('sequence_1_complete'); advanceToNextStep(); }}
                                        onInteraction={trackInteraction}
                                        bgTheme={bgTheme}
                                    />
                                )}

                                {currentStep === 'CATEGORISE_1' && categorisationChunk?.categorisation?.itemsA?.length > 0 && (
                                    <CategorisationGame
                                        key={`categorise-${activeCategorisationChunkIndex}`}
                                        categorisation={categorisationChunk.categorisation}
                                        translationCategoryA={hoverTranslationsEnabled ? (categorisationEnglishChunk?.categorisation?.categoryA || '') : ''}
                                        translationCategoryB={hoverTranslationsEnabled ? (categorisationEnglishChunk?.categorisation?.categoryB || '') : ''}
                                        translationItemsA={hoverTranslationsEnabled ? (categorisationEnglishChunk?.categorisation?.itemsA || []) : []}
                                        translationItemsB={hoverTranslationsEnabled ? (categorisationEnglishChunk?.categorisation?.itemsB || []) : []}
                                        translationHintLanguage={translationHintLanguage}
                                        onResult={handleCategorisationResult}
                                        onComplete={() => { trackInteraction('categorise_1_complete'); advanceToNextStep(); }}
                                        onInteraction={trackInteraction}
                                        bgTheme={bgTheme}
                                    />
                                )}

                                {glossaryPanels.map((panel, panelIndex) => (
                                    currentStep === `GLOSSARY_${panelIndex + 1}` && panel?.terms?.length > 0 && (
                                        <GlossaryMatchingGame
                                            key={`glossary-panel-${panelIndex}`}
                                            terms={panel.terms}
                                            extraDefinitions={panel.decoyDefinitions || []}
                                            onMatchResult={handleGlossaryResult}
                                            onComplete={() => { trackInteraction(`glossary_${panelIndex + 1}_complete`); advanceToNextStep(); }}
                                            onInteraction={trackInteraction}
                                            onCheckExplainer={handleToggleReviewExplainer}
                                            narrationEnabled={isAudioEnabled()}
                                            narrationAudioUrl={
                                                panelIndex === 0
                                                    ? (currentChunk.glossaryNarrationAudioUrlPart1 || currentChunk.glossaryNarrationAudioUrl)
                                                    : panelIndex === 1
                                                        ? (currentChunk.glossaryNarrationAudioUrlPart2 || currentChunk.glossaryNarrationAudioUrl)
                                                        : currentChunk.glossaryNarrationAudioUrl
                                            }
                                            narrationAudioBaseUrl={getFullUrl}
                                            defaultAudioSpeed={defaultAudioSpeed}
                                            showTranslationHints={hoverTranslationsEnabled}
                                            translationHintLanguage={translationHintLanguage}
                                            bgTheme={bgTheme}
                                        />
                                    )
                                ))}

                                {currentStep === 'QUIZ_1' && (
                                    <DynamicQuiz
                                        pool={allMcqs}
                                        ignoreIds={globalUsedQuestionIds}
                                        limit={currentStepPlan.quizLimits[currentStep] || 5}
                                        onAnswerSubmit={handleAnswerSubmit}
                                        onComplete={() => { trackInteraction('quiz_1_complete'); advanceToNextStep(); }}
                                        onInteraction={trackInteraction}
                                        onCheckExplainer={handleToggleReviewExplainer}
                                        narrationEnabled={isAudioEnabled()}
                                        narrationAudioBaseUrl={getFullUrl}
                                        defaultAudioSpeed={defaultAudioSpeed}
                                        enableHoverTranslations={hoverTranslationsEnabled}
                                        translationPairs={glossaryTranslationPairs}
                                        questionTranslationPairs={mcqQuestionTranslationPairs}
                                        optionTranslationPairs={mcqOptionTranslationPairs}
                                        translationHintLanguage={translationHintLanguage}
                                        sourceChunkIndex={currentChunkIndex}
                                        bgTheme={bgTheme}
                                        jcqMode={isJcqMode}
                                    />
                                )}

                                {currentStep.startsWith('QUIZ_') && currentStep !== 'QUIZ_1' && (
                                    <DynamicQuiz
                                        pool={allMcqs}
                                        ignoreIds={globalUsedQuestionIds}
                                        limit={currentStepPlan.quizLimits[currentStep] || 5}
                                        onAnswerSubmit={handleAnswerSubmit}
                                        onComplete={() => { trackInteraction(`${currentStep.toLowerCase()}_complete`); advanceToNextStep(); }}
                                        onInteraction={trackInteraction}
                                        onCheckExplainer={handleToggleReviewExplainer}
                                        narrationEnabled={isAudioEnabled()}
                                        narrationAudioBaseUrl={getFullUrl}
                                        defaultAudioSpeed={defaultAudioSpeed}
                                        enableHoverTranslations={hoverTranslationsEnabled}
                                        translationPairs={glossaryTranslationPairs}
                                        questionTranslationPairs={mcqQuestionTranslationPairs}
                                        optionTranslationPairs={mcqOptionTranslationPairs}
                                        translationHintLanguage={translationHintLanguage}
                                        sourceChunkIndex={currentChunkIndex}
                                        bgTheme={bgTheme}
                                        jcqMode={isJcqMode}
                                    />
                                )}

                            </>
                        )}
                    </div>

                    {isSharedAccess && shouldDockSafetyButtonBelowTutorial && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                            <button
                                type="button"
                                onClick={() => setIsLearningSafetyModalOpen(true)}
                                className="btn-base"
                                style={{
                                    width: isMobileViewport ? '52px' : '58px',
                                    height: isMobileViewport ? '52px' : '58px',
                                    borderRadius: '50%',
                                    padding: 0,
                                    justifyContent: 'center',
                                    background: BRAND.teal,
                                    color: 'white',
                                    boxShadow: '0 8px 18px rgba(45, 64, 66, 0.18)',
                                    fontSize: isMobileViewport ? '1.25rem' : '1.35rem'
                                }}
                                aria-label="Open learning safety information"
                                title="How we keep your learning safe"
                            >
                                🛡️
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT PANE: TEACHER TRACKER */}
            {!isSharedAccess && (<div 
                style={{
                    ...styles.trackerPane, 
                    display: isTrackerOpen ? 'block' : 'none' 
                }} 
                className="hide-scrollbar"
            >
                <div style={styles.trackerHeaderContainer}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <h3 style={{margin:0, fontFamily:'Nunito', fontWeight:'800', color: BRAND.charcoal}}>
                            Teacher Zone
                        </h3>
                        <button 
                            onClick={() => setIsTrackerOpen(false)}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '1.2em',
                                padding: '5px',
                                color: '#999'
                            }}
                            title="Collapse Tracker"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div style={{marginTop:'20px'}}>
                    <div style={{marginBottom: '15px', fontFamily:'Inter', fontSize:'0.95em'}}><strong>Student:</strong> {studentName}</div>
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                         <div style={styles.statBox}>
                             <div style={styles.statLabel}>Content Success</div>
                             <div style={{...styles.statValue, color: substantiveRate >= 80 ? BRAND.success : substantiveRate >= 50 ? BRAND.warning : BRAND.danger}}>{substantiveRate}%</div>
                        </div>
                        <div style={styles.statBox}>
                             <div style={styles.statLabel}>Key Terms</div>
                             <div style={{...styles.statValue, color: keyTermsRate >= 80 ? BRAND.success : keyTermsRate >= 50 ? BRAND.warning : BRAND.danger}}>{keyTermsRate}%</div>
                        </div>
                        <div style={styles.statBox}>
                             <div style={styles.statLabel}>Target</div>
                             <div style={styles.statValue}>80%</div>
                        </div>
                        <div style={styles.statBox}>
                             <div style={styles.statLabel}>Logged</div>
                             <div style={styles.statValue}>{totalAnswersLogged}</div>
                        </div>
                    </div>
                </div>
                
                <hr style={{ border: 0, borderTop: '2px dashed #ddd', margin: '25px 0' }} />
                
                <div style={styles.trackerFeed}>
                    {allAnswers.length === 0 && <p style={{color:'#999', fontStyle:'italic', textAlign:'center'}}>Waiting for first answer...</p>}
                    {allAnswers.length > 0 && [...allAnswers].reverse().map((ans, index) => {
                        let borderColor = ans.isCorrect ? BRAND.success : BRAND.danger;
                        let textColor = ans.isCorrect ? BRAND.success : BRAND.danger;
                        
                        if (ans.questionText && ans.questionText.startsWith('Match:')) {
                            const triesMatch = ans.studentAnswer.match(/Matched in (\d+) tr/);
                            if (triesMatch) {
                                const tries = parseInt(triesMatch[1]);
                                if (tries === 1) { borderColor = BRAND.success; textColor = BRAND.success; } 
                                else if (tries === 2) { borderColor = BRAND.warning; textColor = BRAND.warning; } 
                                else { borderColor = BRAND.danger; textColor = BRAND.danger; }
                            }
                        }
                        return (
                            <div key={index} style={{
                                background: 'white', 
                                border: `2px solid ${BRAND.charcoal}`, 
                                borderLeft: `6px solid ${borderColor}`,
                                borderRadius: '8px', 
                                padding: '15px', 
                                marginBottom: '15px', 
                                boxShadow: '2px 2px 0px rgba(0,0,0,0.05)',
                                position: 'relative'
                            }}>
                                <div style={{fontSize:'0.9em', fontWeight:'bold', marginBottom:'5px', fontFamily:'Nunito', color: BRAND.charcoal}}>
                                    #{allAnswers.length - index} <RenderMarkdown inline>{ans.questionText}</RenderMarkdown>
                                </div>
                                <div style={{color: textColor, fontWeight:'700', fontSize:'0.95em', fontFamily:'Inter'}}>
                                    <RenderMarkdown inline>{ans.studentAnswer}</RenderMarkdown>
                                </div> 
                                {!ans.isCorrect && (
                                    <div style={{fontSize:'0.85em', color:'#666', marginTop:'5px'}}>Correct: <RenderMarkdown inline>{ans.correctAnswer}</RenderMarkdown></div>
                                )}
                                {(ans.timeTaken || studentTutorial.translationApplied) && (
                                    <div style={{position: 'absolute', bottom: '8px', right: '10px', fontSize: '0.75em', color: '#999', fontFamily:'monospace', display: 'flex', alignItems: 'center', gap: '6px'}}>
                                        {studentTutorial?.studentId?.eal && (
                                            <span style={{ background: '#E3F2FD', color: '#1565C0', border: '1px solid #90CAF9', borderRadius: '999px', padding: '1px 6px', fontFamily: 'Inter', fontWeight: '700', fontSize: '0.9em' }}>
                                                EAL
                                            </span>
                                        )}
                                        {ans.timeTaken && <span>{ans.timeTaken}s</span>}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>)}
        </div>
    );
};

const styles = {
    pageContainer: { padding: '40px', maxWidth: '800px', margin: '0 auto', textAlign: 'center', fontFamily: 'Nunito, sans-serif' },
    errorText: { color: BRAND.danger, fontWeight: 'bold', marginBottom: '20px' },
    splitScreenContainer: { display: 'flex', flexDirection: 'row', height: '100vh', overflow:'hidden' },
    playerPane: { 
        flex: 3, 
        padding: '30px', 
        overflowY: 'auto', 
        backgroundColor: BRAND.cream,
        position: 'relative' 
    },
    trackerPane: { 
        flex: 1, 
        padding: '25px', 
        overflowY: 'auto', 
        backgroundColor: 'white', 
        borderLeft: `2px solid ${BRAND.charcoal}`,
        boxShadow: '-4px 0 15px rgba(0,0,0,0.05)',
        zIndex: 2,
        minWidth: '320px'
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '15px' },
    headerTitle: { flex: 1 },
    timerWrapper: { width: '80px', height: '80px' },
    contentArea: { marginBottom: '20px' },
    card: {
        background: 'white',
        border: `2px solid ${BRAND.charcoal}`,
        borderRadius: '16px',
        padding: '30px',
        boxShadow: '4px 4px 0px rgba(45, 64, 66, 0.1)'
    },
    trackerHeaderContainer: { borderBottom: `2px dashed ${BRAND.charcoal}`, paddingBottom: '15px' },
    statBox: { 
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
        background: '#F9FAFB', border: '1px solid #E0E0E0', borderRadius: '8px', padding: '12px 5px' 
    },
    statLabel: { fontSize: '0.7em', color: '#666', marginBottom: '4px', textTransform:'uppercase', fontWeight:'700', letterSpacing:'0.5px' },
    statValue: { fontSize: '1.3em', fontWeight: '800', color: BRAND.charcoal, fontFamily:'Nunito' },
    trackerFeed: { display: 'flex', flexDirection: 'column' },
};

export default StudentTutorialPlayer;
